// Advanced Topics in Fluid Dynamics

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Turbulence Energy Cascade ─────────────────────────
function DiagramEnergyCascade() {
  const W = 900, H = 320;

  // Energy spectrum: E(k) ~ k^(-5/3) in inertial subrange
  // log-log axes: k from 1 to 1000, E from 0.0001 to 10
  const kMin = 0.8, kMax = 800;
  const EMax = 8;

  const kx = (k) => 60 + (Math.log10(k / kMin) / Math.log10(kMax / kMin)) * 740;
  const Ey = (E) => 270 - (Math.log10(Math.max(E, 0.00001)) / Math.log10(EMax / 0.0001)) * 230;

  // E(k) piecewise: production range flat/rising, inertial k^-5/3, dissipation rapid fall
  const specPts = [];
  for (let k = kMin; k <= kMax; k *= 1.04) {
    let E;
    if (k < 2)       E = 2.5 * Math.pow(k / 2, 2);           // energy-containing range (rising)
    else if (k < 4)  E = 2.5;                                  // peak
    else if (k < 200) E = 2.5 * Math.pow(k / 4, -5/3);       // inertial subrange k^{-5/3}
    else              E = 2.5 * Math.pow(200/4, -5/3) * Math.exp(-(k-200)/60); // dissipation
    specPts.push({ k, E });
  }
  const specPath = specPts.map(({ k, E }, i) => `${i === 0 ? 'M' : 'L'} ${kx(k)},${Ey(E)}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Turbulent Energy Spectrum — Kolmogorov Energy Cascade</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="axArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#333"/>
          </marker>
          <marker id="ecArrR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="ecArrO" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#e07b00"/>
          </marker>
        </defs>

        {/* Axes */}
        <line x1={60} y1={270} x2={820} y2={270} stroke="#333" strokeWidth="1.8" markerEnd="url(#axArr)"/>
        <line x1={60} y1={270} x2={60}  y2={22}  stroke="#333" strokeWidth="1.8" markerEnd="url(#axArr)"/>
        <text x={828} y={274} fontSize="12" fill="#333" fontWeight="700">k (wavenumber)</text>
        <text x={48}  y={18}  fontSize="12" fill="#333" fontWeight="700">E(k)</text>
        <text x={44}  y={32}  fontSize="10" fill="#888">(log)</text>
        <text x={700} y={285} fontSize="10" fill="#888">(log scale)</text>

        {/* X-axis tick labels */}
        {[1, 10, 100].map(v => (
          <g key={v}>
            <line x1={kx(v)} y1={267} x2={kx(v)} y2={273} stroke="#333" strokeWidth="1.2"/>
            <text x={kx(v)} y={284} textAnchor="middle" fontSize="11" fill="#555">{v}</text>
          </g>
        ))}

        {/* Spectrum curve */}
        <path d={specPath} fill="none" stroke="#c0392b" strokeWidth="3"/>

        {/* -5/3 slope reference line */}
        {(()=>{
          const k1 = 5, k2 = 160;
          return (
            <line x1={kx(k1)} y1={Ey(2.5 * Math.pow(k1/4, -5/3))}
                  x2={kx(k2)} y2={Ey(2.5 * Math.pow(k2/4, -5/3))}
              stroke="#9b59b6" strokeWidth="1.8" strokeDasharray="7,4"/>
          );
        })()}
        <text x={kx(30)} y={Ey(2.5 * Math.pow(30/4,-5/3))-12} textAnchor="middle"
          fontSize="13" fill="#9b59b6" fontWeight="700">slope = −5/3</text>

        {/* Region shading bands */}
        {/* Energy-containing */}
        <rect x={kx(kMin)} y={25} width={kx(4)-kx(kMin)} height={240} fill="#e8f4e8" opacity="0.35"/>
        <text x={(kx(kMin)+kx(4))/2} y={45} textAnchor="middle" fontSize="12" fill="#145214" fontWeight="700">Energy-containing</text>
        <text x={(kx(kMin)+kx(4))/2} y={59} textAnchor="middle" fontSize="11" fill="#145214">large eddies</text>
        <text x={(kx(kMin)+kx(4))/2} y={73} textAnchor="middle" fontSize="11" fill="#145214">L ~ integral scale</text>
        <text x={(kx(kMin)+kx(4))/2} y={87} textAnchor="middle" fontSize="11" fill="#145214">Re-dependent</text>

        {/* Inertial subrange */}
        <rect x={kx(4)} y={25} width={kx(200)-kx(4)} height={240} fill="#FEF0F0" opacity="0.35"/>
        <text x={(kx(4)+kx(200))/2} y={45} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Inertial Subrange</text>
        <text x={(kx(4)+kx(200))/2} y={59} textAnchor="middle" fontSize="11" fill="#7B0D0D">E(k) = C ε^(2/3) k^(−5/3)</text>
        <text x={(kx(4)+kx(200))/2} y={73} textAnchor="middle" fontSize="11" fill="#7B0D0D">Kolmogorov 1941</text>
        <text x={(kx(4)+kx(200))/2} y={87} textAnchor="middle" fontSize="11" fill="#7B0D0D">universal, Re-independent</text>

        {/* Dissipation range */}
        <rect x={kx(200)} y={25} width={kx(kMax)-kx(200)} height={240} fill="#fff5e6" opacity="0.4"/>
        <text x={(kx(200)+kx(kMax))/2} y={45} textAnchor="middle" fontSize="12" fill="#8B4513" fontWeight="700">Dissipation Range</text>
        <text x={(kx(200)+kx(kMax))/2} y={59} textAnchor="middle" fontSize="11" fill="#8B4513">η = (ν³/ε)^(1/4)</text>
        <text x={(kx(200)+kx(kMax))/2} y={73} textAnchor="middle" fontSize="11" fill="#8B4513">Kolmogorov micro-scale</text>
        <text x={(kx(200)+kx(kMax))/2} y={87} textAnchor="middle" fontSize="11" fill="#8B4513">viscosity kills energy</text>

        {/* Cascade arrows */}
        <line x1={kx(1.5)} y1={160} x2={kx(400)} y2={160}
          stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#ecArrR)"/>
        <text x={(kx(1.5)+kx(400))/2} y={155} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">Energy cascade: large eddies → small eddies → heat</text>

        {/* ε label */}
        <text x={kx(30)} y={270+16} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">ε (dissipation rate) sets cascade rate throughout</text>

        {/* Right panel: cascade sketch */}
        <rect x={700} y={95} width={188} height={168} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={794} y={112} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Physical Picture</text>
        {/* Large eddy */}
        <ellipse cx={750} cy={155} rx={38} ry={28} fill="none" stroke="#27ae60" strokeWidth="2.5" strokeDasharray="5,3"/>
        <text x={750} y={180} textAnchor="middle" fontSize="10" fill="#27ae60">Large eddy</text>
        {/* Medium eddies */}
        <ellipse cx={808} cy={140} rx={20} ry={15} fill="none" stroke="#e07b00" strokeWidth="2"/>
        <ellipse cx={832} cy={162} rx={12} ry={9}  fill="none" stroke="#e07b00" strokeWidth="1.8"/>
        <text x={845} y={178} textAnchor="middle" fontSize="10" fill="#e07b00">smaller</text>
        {/* Tiny eddies */}
        <ellipse cx={856} cy={130} rx={7} ry={5} fill="none" stroke="#c0392b" strokeWidth="1.5"/>
        <ellipse cx={870} cy={148} rx={4} ry={3} fill="#c0392b" opacity="0.4"/>
        <text x={870} y={118} textAnchor="middle" fontSize="9" fill="#c0392b">→ heat</text>
        {/* Arrows between eddies */}
        <line x1={788} y1={148} x2={800} y2={143} stroke="#888" strokeWidth="1.2" markerEnd="url(#ecArrO)"/>
        <line x1={824} y1={155} x2={836} y2={152} stroke="#888" strokeWidth="1.2" markerEnd="url(#ecArrO)"/>
      </svg>
      <div style={S.dCaption}>Turbulent kinetic energy is injected at large scales (integral length scale L, set by geometry) and transferred to progressively smaller scales through the energy cascade — a process in which large eddies break up into smaller ones. In the inertial subrange (Kolmogorov 1941), viscosity is negligible and the spectrum is universal: E(k) = Cε^(2/3)k^(−5/3), where ε is the turbulent dissipation rate and C ≈ 1.5. At the Kolmogorov microscale η = (ν³/ε)^(1/4), viscosity converts the remaining kinetic energy into heat. This scale separation — often 3–6 orders of magnitude between L and η — is why directly resolving all turbulence (DNS) is only feasible at modest Reynolds numbers.</div>
    </div>
  );
}

// ── Diagram 2: Reynolds Averaging & Turbulence Models ────────────
function DiagramRANS() {
  const W = 900, H = 310;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Reynolds Decomposition and the RANS Closure Problem</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="raArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
        </defs>

        {/* Left: Reynolds decomposition time series */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Reynolds Decomposition:  u = Ū + u′</text>

        {(()=>{
          const ox=30, oy=50, pw=PW-40, ch=120;
          const tx = (t) => ox + (t / 100) * pw;
          const uy = (u) => oy + ch/2 - u * (ch/2) * 0.85;

          // Generate turbulent signal: mean 0.6 + random fluctuations
          const nPts = 200;
          // Use a simple deterministic pseudo-random for reproducibility
          const signal = [];
          let seed = 42;
          for (let i = 0; i <= nPts; i++) {
            seed = (seed * 1664525 + 1013904223) & 0xffffffff;
            const r = (seed >>> 0) / 0xffffffff;
            const t = i / nPts * 100;
            const fluc = (r - 0.5) * 1.8 + 0.3 * Math.sin(i * 0.5) + 0.2 * Math.sin(i * 1.3);
            signal.push({ t, u: 0.6 + fluc });
          }

          const sigPath = signal.map(({ t, u }, i) => `${i === 0 ? 'M' : 'L'} ${tx(t)},${uy(u)}`).join(' ');
          const meanPath = `M ${tx(0)},${uy(0.6)} L ${tx(100)},${uy(0.6)}`;

          return <>
            {/* Axes */}
            <line x1={ox} y1={oy+ch} x2={ox+pw+10} y2={oy+ch} stroke="#333" strokeWidth="1.5"/>
            <line x1={ox} y1={oy}    x2={ox}       y2={oy+ch+6} stroke="#333" strokeWidth="1.5"/>
            <text x={ox+pw+14} y={oy+ch+4} fontSize="11" fill="#333">t</text>
            <text x={ox-8}     y={oy-4}    fontSize="11" fill="#333">u</text>

            {/* Turbulent signal */}
            <path d={sigPath} fill="none" stroke="#c0392b" strokeWidth="1.5" opacity="0.75"/>
            {/* Mean */}
            <path d={meanPath} fill="none" stroke="#2980b9" strokeWidth="2.5"/>
            <text x={ox+pw+4} y={uy(0.6)+4} fontSize="12" fill="#2980b9" fontWeight="700">Ū</text>
            <text x={ox+pw+4} y={uy(0.6)+17} fontSize="11" fill="#2980b9">(mean)</text>

            {/* Fluctuation bracket */}
            {(()=>{
              const tSamp = 38, uSamp = signal[Math.round(tSamp * nPts / 100)].u;
              const xS = tx(tSamp), yMean = uy(0.6), yU = uy(uSamp);
              return <>
                <line x1={xS} y1={yMean} x2={xS} y2={yU} stroke="#e07b00" strokeWidth="2.5" strokeDasharray="4,2"/>
                <circle cx={xS} cy={yU} r={4} fill="#c0392b"/>
                <text x={xS+6} y={(yMean+yU)/2+4} fontSize="12" fill="#e07b00" fontWeight="700">u′</text>
                <text x={xS+6} y={(yMean+yU)/2+17} fontSize="11" fill="#e07b00">(fluctuation)</text>
              </>;
            })()}

            {/* Legend */}
            <line x1={ox+6} y1={oy+ch+22} x2={ox+26} y2={oy+ch+22} stroke="#c0392b" strokeWidth="1.5"/>
            <text x={ox+30} y={oy+ch+26} fontSize="11" fill="#c0392b">u = Ū+u′ (instantaneous)</text>
            <line x1={ox+6} y1={oy+ch+38} x2={ox+26} y2={oy+ch+38} stroke="#2980b9" strokeWidth="2.5"/>
            <text x={ox+30} y={oy+ch+42} fontSize="11" fill="#2980b9">Ū (time-averaged mean)</text>
            <line x1={ox+6} y1={oy+ch+54} x2={ox+26} y2={oy+ch+54} stroke="#e07b00" strokeWidth="2.5" strokeDasharray="4,2"/>
            <text x={ox+30} y={oy+ch+58} fontSize="11" fill="#e07b00">u′ (turbulent fluctuation, mean=0)</text>
          </>;
        })()}

        {/* Right: Closure hierarchy diagram */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Turbulence Model Hierarchy</text>

        {(()=>{
          const levels = [
            { y:50,  w:380, fill:'#FEF0F0', stroke:'#c0392b',  label:'DNS  (Direct Numerical Simulation)',         sub:'No model — resolve ALL scales; cost ~ Re^3; Re < 5000 only' },
            { y:100, w:380, fill:'#fff5e6', stroke:'#e07b00',  label:'LES  (Large Eddy Simulation)',               sub:'Resolve large eddies; model sub-grid scales (SGS); Re < 10⁵' },
            { y:150, w:380, fill:'#f5e6ff', stroke:'#9b59b6',  label:'DES / SAS  (Hybrid RANS-LES)',               sub:'RANS near wall, LES away; practical for separated flows' },
            { y:200, w:380, fill:'#e8f4e8', stroke:'#27ae60',  label:'RANS  (Reynolds-Averaged)',                  sub:'Model ALL turbulence; workhorse of industry; steady/unsteady' },
            { y:250, w:380, fill:'#fff',    stroke:'#555',     label:'Algebraic / mixing-length models',           sub:'Simplest closure; Prandtl mixing length; boundary layers only' },
          ];
          const ox = PW+30;

          return <>
            {/* Accuracy arrow */}
            <line x1={ox+390} y1={270} x2={ox+390} y2={44}
              stroke="#c0392b" strokeWidth="2" markerEnd="url(#raArr)"/>
            <text x={ox+396} y={160} fontSize="11" fill="#c0392b" fontWeight="700"
              transform={`rotate(90,${ox+396},160)`}>Accuracy ↑</text>
            {/* Cost arrow (opposite) */}
            <line x1={ox+406} y1={44} x2={ox+406} y2={270}
              stroke="#e07b00" strokeWidth="2" markerEnd="url(#raArr)"/>
            <text x={ox+412} y={160} fontSize="11" fill="#e07b00" fontWeight="700"
              transform={`rotate(90,${ox+412},160)`}>Cost ↑</text>

            {levels.map(({ y, w, fill, stroke, label, sub }) => (
              <g key={label}>
                <rect x={ox} y={y} width={w} height={44} rx={3} fill={fill} stroke={stroke} strokeWidth="1.5"/>
                <text x={ox+8} y={y+16} fontSize="12" fill="#333" fontWeight="700">{label}</text>
                <text x={ox+8} y={y+32} fontSize="10" fill="#555">{sub}</text>
              </g>
            ))}
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Reynolds decomposition splits the instantaneous velocity into a time-averaged mean Ū and a turbulent fluctuation u′ (with zero mean). Substituting into the Navier–Stokes equations produces the RANS equations, which are identical in form to the original but contain an extra term: the Reynolds stress tensor −ρ⭨u′ᵢu′ⱼ⭩. This contains six unknown correlations — the closure problem. All turbulence models provide algebraic or transport-equation approximations for these stresses. DNS resolves everything (no model) but is computationally prohibitive; LES resolves large eddies and models small ones; RANS models all turbulence and remains the practical standard for engineering computations.</div>
    </div>
  );
}

// ── Diagram 3: k-ε and k-ω Turbulence Models ────────────────────
function DiagramTurbulenceModels() {
  const W = 900, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Two-Equation Turbulence Models — k-ε and k-ω Transport Equations</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* k-ε panel */}
        <rect x={10} y={10} width={425} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={222} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">k–ε Model  (Launder &amp; Spalding, 1974)</text>

        {/* TKE equation k */}
        <rect x={20} y={38} width={405} height={48} rx={3} fill="#fff5f5" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={28} y={55} fontSize="11" fill="#7B0D0D" fontWeight="700">TKE transport equation:</text>
        <text x={28} y={72} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">∂k/∂t + Ū·∇k = Pk − ε + ∇·[(ν+νt/σk)∇k]</text>

        {/* ε equation */}
        <rect x={20} y={94} width={405} height={48} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={28} y={111} fontSize="11" fill="#8B4513" fontWeight="700">Dissipation rate equation:</text>
        <text x={28} y={128} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">∂ε/∂t + Ū·∇ε = Cε1(ε/k)Pk − Cε2(ε²/k) + ∇·[(ν+νt/σε)∇ε]</text>

        {/* Closure */}
        <rect x={20} y={150} width={405} height={58} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={28} y={167} fontSize="11" fill="#145214" fontWeight="700">Closure (Boussinesq hypothesis):</text>
        <text x={28} y={183} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">νt = Cμ k²/ε      (turbulent viscosity)</text>
        <text x={28} y={199} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">−⭨u′iu′j⭩ = νt(∂Ūi/∂xj + ∂Ūj/∂xi) − (2/3)k δij</text>

        {/* Constants */}
        <rect x={20} y={216} width={405} height={38} rx={3} fill="#f5e6ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={28} y={231} fontSize="11" fill="#5b2c8d" fontWeight="700">Standard constants:</text>
        <text x={28} y={246} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Cμ=0.09, Cε1=1.44, Cε2=1.92, σk=1.0, σε=1.3</text>

        {/* Strengths / weaknesses */}
        <rect x={20} y={262} width={405} height={26} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={28} y={278} fontSize="10" fill="#7B0D0D">✓ Robust, widely validated  ✗ Poor for adverse pressure gradients, rotating flows, separation</text>

        {/* k-ω panel */}
        <rect x={445} y={10} width={445} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={667} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">k–ω SST Model  (Menter, 1994)</text>

        {/* k equation */}
        <rect x={455} y={38} width={425} height={48} rx={3} fill="#fff5f5" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={463} y={55} fontSize="11" fill="#7B0D0D" fontWeight="700">TKE transport equation:</text>
        <text x={463} y={72} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">∂k/∂t + Ū·∇k = Pk − β*kω + ∇·[(ν+σkνt)∇k]</text>

        {/* ω equation */}
        <rect x={455} y={94} width={425} height={48} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={463} y={111} fontSize="11" fill="#8B4513" fontWeight="700">Specific dissipation rate equation:</text>
        <text x={463} y={128} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">∂ω/∂t + Ū·∇ω = γ(Pk/νt) − βω² + ∇·[(ν+σωνt)∇ω] + CDkω</text>

        {/* SST blending */}
        <rect x={455} y={150} width={425} height={58} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={463} y={167} fontSize="11" fill="#145214" fontWeight="700">SST blending function F1:</text>
        <text x={463} y={183} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">F1 = 1 near wall  (k–ω activated)</text>
        <text x={463} y={199} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">F1 = 0 far from wall  (k–ε activated via cross-diff.)</text>

        {/* νt with limiter */}
        <rect x={455} y={216} width={425} height={38} rx={3} fill="#f5e6ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={463} y={231} fontSize="11" fill="#5b2c8d" fontWeight="700">Turbulent viscosity with shear-stress limiter:</text>
        <text x={463} y={246} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">νt = a1 k / max(a1ω, S F2)   prevents over-pred. in APG</text>

        {/* Strengths */}
        <rect x={455} y={262} width={425} height={26} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
        <text x={463} y={278} fontSize="10" fill="#145214">✓ Accurate in adverse pressure gradients, separation  ✓ Industry standard for aerodynamics  ✗ Slightly more expensive than k-ε</text>
      </svg>
      <div style={S.dCaption}>Two-equation models solve transport equations for two turbulent quantities, providing both a velocity scale (k) and a length scale (ε or ω) without prescribing them algebraically. The k-ε model is robust and numerically stable, making it the workhorse for industrial flows; its weakness is over-predicting eddy viscosity in adverse pressure gradients and near separated flows. The k-ω SST (Shear Stress Transport) model of Menter (1994) blends k-ω near walls (where it is more accurate) with k-ε in the free stream (where k-ω is sensitive to free-stream ω), and adds a limiter on νt to prevent over-prediction of turbulent stresses in regions of adverse pressure gradient — making it the most widely used model in aerospace and turbomachinery CFD today.</div>
    </div>
  );
}

// ── Diagram 4: Potential Flow — Elementary Flows ─────────────────
function DiagramPotentialFlow() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Potential Flow — Superposition of Elementary Solutions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* ── Uniform flow ── */}
        <rect x={10}  y={10} width={170} height={290} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={95}  y={27} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Uniform Flow</text>
        <text x={95}  y={41} textAnchor="middle" fontSize="11" fill="#555">ψ = U∞ y</text>
        <text x={95}  y={55} textAnchor="middle" fontSize="11" fill="#555">φ = U∞ x</text>
        {[70,100,130,160,190,220,250].map(y => (
          <line key={y} x1={18} y1={y} x2={172} y2={y}
            stroke="#2980b9" strokeWidth="1.5" opacity="0.7"
            markerEnd="url(#pfArr)"/>
        ))}
        <text x={95} y={275} textAnchor="middle" fontSize="10" fill="#555">Parallel streamlines</text>
        <text x={95} y={289} textAnchor="middle" fontSize="10" fill="#555">const. velocity U∞</text>

        {/* ── Source / Sink ── */}
        <rect x={188} y={10} width={170} height={290} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={273} y={27} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Source  (m &gt; 0)</text>
        <text x={273} y={41} textAnchor="middle" fontSize="11" fill="#555">ψ = (m/2π) θ</text>
        <text x={273} y={55} textAnchor="middle" fontSize="11" fill="#555">φ = (m/2π) ln r</text>
        {(()=>{
          const cx=273, cy=165;
          return Array.from({length:12},(_,i)=>{
            const a = i * 30 * Math.PI / 180;
            return <line key={i} x1={cx} y1={cy}
              x2={cx + 70*Math.cos(a)} y2={cy + 70*Math.sin(a)}
              stroke="#27ae60" strokeWidth="1.5" opacity="0.8"
              markerEnd="url(#pfArrG)"/>;
          });
        })()}
        <circle cx={273} cy={165} r={5} fill="#c0392b"/>
        <text x={273} y={275} textAnchor="middle" fontSize="10" fill="#555">Radial outflow</text>
        <text x={273} y={289} textAnchor="middle" fontSize="10" fill="#555">V_r = m/(2πr)</text>

        {/* ── Free vortex ── */}
        <rect x={366} y={10} width={170} height={290} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={451} y={27} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Free Vortex</text>
        <text x={451} y={41} textAnchor="middle" fontSize="11" fill="#555">ψ = −(Γ/2π) ln r</text>
        <text x={451} y={55} textAnchor="middle" fontSize="11" fill="#555">φ = (Γ/2π) θ</text>
        {(()=>{
          const cx=451, cy=165;
          return [25,45,60,75].map(r=>(
            <circle key={r} cx={cx} cy={cy} r={r}
              fill="none" stroke="#9b59b6" strokeWidth="1.5" opacity="0.7"/>
          ));
        })()}
        {/* Rotation arrows */}
        {(()=>{
          const cx=451, cy=165;
          return [35,60].map(r=>(
            <path key={r}
              d={`M ${cx+r},${cy} A ${r} ${r} 0 1 1 ${cx-r},${cy}`}
              fill="none" stroke="#9b59b6" strokeWidth="1.5" markerEnd="url(#pfArrV)"/>
          ));
        })()}
        <circle cx={451} cy={165} r={5} fill="#c0392b"/>
        <text x={451} y={275} textAnchor="middle" fontSize="10" fill="#555">Irrotational swirl</text>
        <text x={451} y={289} textAnchor="middle" fontSize="10" fill="#555">V_θ = Γ/(2πr)</text>

        {/* ── Doublet ── */}
        <rect x={544} y={10} width={170} height={290} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={629} y={27} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Doublet</text>
        <text x={629} y={41} textAnchor="middle" fontSize="11" fill="#555">ψ = −(κ/2π) y/r²</text>
        <text x={629} y={55} textAnchor="middle" fontSize="11" fill="#555">φ = (κ/2π) x/r²</text>
        {(()=>{
          const cx=629, cy=165;
          // Doublet streamlines: circles through origin, ψ = const → (x-a)²+y²=a²
          return [15,28,42,58].map(a=>(
            <g key={a}>
              <circle cx={cx+a} cy={cy} r={a} fill="none" stroke="#e07b00" strokeWidth="1.4" opacity="0.7"/>
              <circle cx={cx-a} cy={cy} r={a} fill="none" stroke="#e07b00" strokeWidth="1.4" opacity="0.7"/>
            </g>
          ));
        })()}
        <circle cx={629} cy={165} r={5} fill="#c0392b"/>
        <text x={629} y={275} textAnchor="middle" fontSize="10" fill="#555">Source + Sink limit</text>
        <text x={629} y={289} textAnchor="middle" fontSize="10" fill="#555">κ = source strength × d</text>

        {/* ── Rankine Cylinder (superposition) ── */}
        <rect x={722} y={10} width={168} height={290} rx={4} fill="#fff5f5" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={806} y={27} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Rankine Cylinder</text>
        <text x={806} y={41} textAnchor="middle" fontSize="11" fill="#7B0D0D">Uniform + Doublet</text>
        <text x={806} y={55} textAnchor="middle" fontSize="10" fill="#555">ψ = U∞ r sinθ(1−R²/r²)</text>
        {(()=>{
          const cx=806, cy=155, R=42;
          // Streamlines around cylinder
          const lines = [
            [-65,-60,-55,-45,-35,-28],  // y offsets for streamlines
          ][0];
          return <>
            {[...lines, ...lines.map(v=>-v)].map((dy,i)=>{
              const y0 = cy + dy;
              // Simple curved path approximation
              const midBump = dy > 0 ? -8 : 8;
              return <path key={i}
                d={`M ${cx-78},${y0} C ${cx-30},${y0} ${cx-R-5},${y0+midBump*0.3} ${cx-R*0.7},${y0+midBump*0.8} C ${cx-R*0.3},${y0+midBump} ${cx+R*0.3},${y0+midBump} ${cx+R*0.7},${y0+midBump*0.8} C ${cx+R+5},${y0+midBump*0.3} ${cx+30},${y0} ${cx+78},${y0}`}
                fill="none" stroke="#2980b9" strokeWidth="1.3" opacity="0.65"/>;
            })}
            <circle cx={cx} cy={cy} r={R} fill="#e8c4c4" stroke="#c0392b" strokeWidth="2"/>
            <text x={cx} y={cy+5} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">R²=κ/U∞</text>
          </>;
        })()}
        <text x={806} y={275} textAnchor="middle" fontSize="10" fill="#7B0D0D">No drag (d'Alembert)</text>
        <text x={806} y={289} textAnchor="middle" fontSize="10" fill="#7B0D0D">+ vortex → Joukowski lift</text>

        <defs>
          <marker id="pfArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#2980b9"/>
          </marker>
          <marker id="pfArrG" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#27ae60"/>
          </marker>
          <marker id="pfArrV" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#9b59b6"/>
          </marker>
        </defs>
      </svg>
      <div style={S.dCaption}>Potential flow assumes irrotational (ω = ∇×u = 0), inviscid, incompressible flow. The velocity is the gradient of a scalar potential φ (u = ∇φ) and Laplace's equation ∇²φ = 0 governs the field — a linear PDE, so solutions superpose. The five elementary solutions — uniform flow, source/sink, free vortex, doublet, and their combinations — can be superposed to model any geometry. The Rankine cylinder (uniform flow + doublet) gives the exact irrotational flow around a cylinder; adding a free vortex gives lift via the Kutta–Joukowski theorem L = ρU∞Γ. Potential flow fails where viscosity matters (boundary layers, separated regions) but provides excellent outer-flow boundary conditions and analytical insight.</div>
    </div>
  );
}

// ── Diagram 5: Joukowski Transform & Aerofoil ────────────────────
function DiagramJoukowski() {
  const W = 900, H = 290;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Joukowski Transform — Mapping a Cylinder to an Aerofoil</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left: z-plane (cylinder) */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">z-plane  (circle + uniform flow)</text>

        {(()=>{
          const cx=200, cy=145, R=72;
          // Streamlines in z-plane around cylinder (Rankine + vortex for lift)
          const yOffsets = [-65,-50,-38,-28,-20,20,28,38,50,65];
          return <>
            {/* Axes */}
            <line x1={cx-130} y1={cy} x2={cx+130} y2={cy} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>
            <line x1={cx} y1={cy-120} x2={cx} y2={cy+120} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>
            <text x={cx+135} y={cy+4} fontSize="11" fill="#aaa">Re(z)</text>
            <text x={cx+4} y={cy-124} fontSize="11" fill="#aaa">Im(z)</text>

            {/* Cylinder */}
            <circle cx={cx} cy={cy} r={R} fill="#e8c4c4" stroke="#c0392b" strokeWidth="2"/>
            <text x={cx} y={cy+5} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">|z|=R</text>

            {/* Streamlines with asymmetric upwash (vortex adds) */}
            {yOffsets.map((dy,i)=>{
              const y0=cy+dy;
              const sign = dy < 0 ? -1 : 1;
              const lift = -sign * 8;
              return <path key={i}
                d={`M ${cx-145},${y0} C ${cx-60},${y0} ${cx-R-5},${y0+lift*0.5} ${cx-R*0.5},${y0+lift} C ${cx},${y0+lift*1.1} ${cx+R*0.5},${y0+lift} ${cx+R+5},${y0+lift*0.5} C ${cx+60},${y0} ${cx+100},${y0} ${cx+145},${y0}`}
                fill="none" stroke="#2980b9" strokeWidth="1.3" opacity="0.65"/>;
            })}

            {/* Stagnation points */}
            <circle cx={cx-R} cy={cy+10} r={4} fill="#e07b00"/>
            <circle cx={cx+R} cy={cy-10} r={4} fill="#e07b00"/>
            <text x={cx-R-10} y={cy+26} textAnchor="end" fontSize="10" fill="#e07b00">S₁</text>
            <text x={cx+R+6}  y={cy-14} fontSize="10" fill="#e07b00">S₂</text>

            {/* Kutta condition label */}
            <text x={cx} y={cy+110} textAnchor="middle" fontSize="11" fill="#9b59b6">Γ chosen by Kutta condition</text>
          </>;
        })()}

        {/* Arrow in middle */}
        <text x={450} y={135} textAnchor="middle" fontSize="22" fill="#c0392b">⟹</text>
        <text x={450} y={154} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Joukowski</text>
        <text x={450} y={168} textAnchor="middle" fontSize="11" fill="#555">w = z + c²/z</text>

        {/* Right: w-plane (aerofoil) */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">w-plane  (Joukowski aerofoil)</text>

        {(()=>{
          const cx=690, cy=148;
          // Joukowski aerofoil shape: approximate
          const nPts=60;
          const points=[];
          for(let i=0;i<=nPts;i++){
            const t=i/nPts;
            // Parametric aerofoil approximation
            const angle=2*Math.PI*t;
            const R=72, eps=0.08, c=2*R*(1-eps);
            const zx=(R+eps*R)*Math.cos(angle);
            const zy=(R+eps*R)*Math.sin(angle)+eps*R*0.5;
            const r2=zx*zx+zy*zy;
            const wx=zx+c*c*zx/r2;
            const wy=zy-c*c*zy/r2;
            points.push({x:cx+wx*0.55, y:cy-wy*0.55});
          }
          const aeroPath=points.map(({x,y},i)=>`${i===0?'M':'L'} ${x},${y}`).join(' ')+ ' Z';

          // Streamlines over aerofoil
          const yOffsets=[-55,-40,-28,-18,18,28,40,55];
          return <>
            {/* Streamlines */}
            {yOffsets.map((dy,i)=>{
              const y0=cy+dy;
              const sign = dy < 0 ? -1 : 1;
              const lift = -sign * 10;
              return <path key={i}
                d={`M ${cx-155},${y0} C ${cx-60},${y0} ${cx-30},${y0+lift*0.6} ${cx},${y0+lift} C ${cx+40},${y0+lift*0.6} ${cx+80},${y0} ${cx+155},${y0}`}
                fill="none" stroke="#2980b9" strokeWidth="1.3" opacity="0.6"/>;
            })}
            {/* Aerofoil */}
            <path d={aeroPath} fill="#e8c4c4" stroke="#c0392b" strokeWidth="2"/>
            <text x={cx} y={cy+5} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Aerofoil</text>

            {/* Lift arrow */}
            <line x1={cx+20} y1={cy-60} x2={cx+20} y2={cy-100}
              stroke="#27ae60" strokeWidth="3"
              markerEnd="url(#pfArrG)"/>
            <text x={cx+26} y={cy-78} fontSize="12" fill="#27ae60" fontWeight="700">L = ρU∞Γ</text>

            {/* Sharp trailing edge label */}
            <circle cx={cx+88} cy={cy+4} r={4} fill="#e07b00"/>
            <text x={cx+94} y={cy+2} fontSize="10" fill="#e07b00">Trailing edge</text>
            <text x={cx+94} y={cy+14} fontSize="10" fill="#e07b00">(Kutta condition)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>The Joukowski conformal mapping w = z + c²/z transforms the flow around a cylinder (z-plane) into the flow around an aerofoil (w-plane). Because conformal maps preserve the Laplace equation, the potential flow solution in the z-plane maps exactly to the solution in the w-plane. The Kutta condition — that the flow must leave smoothly from the sharp trailing edge — uniquely determines the circulation Γ and hence the lift L = ρU∞Γ. The Joukowski transform provided the first analytical theory of aerofoil lift (Joukowski 1910) and remains a cornerstone of thin aerofoil theory and panel methods used in modern aerodynamic design.</div>
    </div>
  );
}

// ── Diagram 6: CFD Pipeline ───────────────────────────────────────
function DiagramCFDPipeline() {
  const W = 900, H = 300;

  const steps = [
    { x:20,  w:140, label:'Geometry &\nCAD Model',     fill:'#e8f4e8', stroke:'#27ae60',   tc:'#145214',  detail:'CAD import\ndefect healing\nsurface prep' },
    { x:180, w:140, label:'Meshing',                    fill:'#fff5e6', stroke:'#e07b00',   tc:'#8B4513',  detail:'Structured/\nunstructured\nwall y+ control' },
    { x:340, w:140, label:'Physics Setup',              fill:'#f5e6ff', stroke:'#9b59b6',   tc:'#5b2c8d',  detail:'Turbulence\nBCs, ICs\nfluid properties' },
    { x:500, w:140, label:'Solver',                     fill:'#FEF0F0', stroke:'#c0392b',   tc:'#7B0D0D',  detail:'RANS / LES\nFV discretisation\niterative solver' },
    { x:660, w:140, label:'Post-Processing',            fill:'#eaf3ff', stroke:'#2980b9',   tc:'#1a3a5c',  detail:'Streamlines\nforces, pressures\nvalidation' },
    { x:820, w:60,  label:'↩ Refine',                   fill:'#FEF0F0', stroke:'#c0392b',   tc:'#7B0D0D',  detail:'' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>CFD Workflow — From Geometry to Result</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="cfArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#555"/>
          </marker>
          <marker id="cfArrR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
        </defs>

        {/* Step boxes + arrows */}
        {steps.map(({ x, w, label, fill, stroke, tc, detail }, i) => {
          const lines = label.split('\n');
          const detLines = detail.split('\n');
          return (
            <g key={i}>
              <rect x={x} y={40} width={w} height={56} rx={5} fill={fill} stroke={stroke} strokeWidth="2"/>
              {lines.map((l, j) => (
                <text key={j} x={x + w/2} y={64 + j*16} textAnchor="middle" fontSize="12" fill={tc} fontWeight="700">{l}</text>
              ))}
              {/* Detail below */}
              {detLines.map((l, j) => (
                <text key={j} x={x + w/2} y={114 + j*14} textAnchor="middle" fontSize="10" fill="#555">{l}</text>
              ))}
              {/* Arrow to next */}
              {i < steps.length - 1 && i < 4 && (
                <line x1={x + w} y1={68} x2={x + w + 18} y2={68}
                  stroke="#555" strokeWidth="2" markerEnd="url(#cfArr)"/>
              )}
            </g>
          );
        })}

        {/* Feedback arrow */}
        <path d={`M 820,68 C 860,68 876,68 876,210 C 876,260 100,260 20,210 C 20,180 20,96 20,96`}
          fill="none" stroke="#c0392b" strokeWidth="1.8" strokeDasharray="6,4" markerEnd="url(#cfArrR)"/>
        <text x={448} y={256} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Mesh refinement / model adjustment loop</text>

        {/* Key challenge boxes */}
        {[
          { x:20,  y:160, w:145, text:'• Water-tight geometry\n• Feature resolution\n• Tolerances ≤ 1e-6 m' },
          { x:180, y:160, w:145, text:'• Wall y+ ≈ 1 (SST)\n• y+ ≈ 30-100 (wall fn)\n• Grid independence' },
          { x:340, y:160, w:145, text:'• Turbulence model\n• Inflow turbulence\n• Convergence criteria' },
          { x:500, y:160, w:145, text:'• Residuals < 1e-5\n• Monitor key quantities\n• Courant number (LES)' },
          { x:660, y:160, w:145, text:'• GCI uncertainty\n• Compare experiment\n• Sensitivity study' },
        ].map(({ x, y, w, text }) => (
          <g key={x}>
            <rect x={x} y={y} width={w} height={58} rx={3} fill="#fffafa" stroke="#f0cece"/>
            {text.split('\n').map((l, j) => (
              <text key={j} x={x+6} y={y+16+j*14} fontSize="10" fill="#444">{l}</text>
            ))}
          </g>
        ))}

        {/* Title row labels */}
        <text x={448} y={28} textAnchor="middle" fontSize="12" fill="#555">Key challenges per step:</text>
      </svg>
      <div style={S.dCaption}>A CFD simulation follows five stages: geometry preparation (watertight CAD, defect removal), mesh generation (structured or unstructured cells, wall-normal resolution for boundary layer), physics setup (turbulence model, boundary conditions, fluid properties), the iterative solver (finite volume/element/difference discretisation, pressure-velocity coupling), and post-processing with rigorous validation against experiment or analytic solutions. The Grid Convergence Index (GCI) methodology quantifies numerical discretisation uncertainty. Practically, most CFD effort goes into meshing (~50%) and validation (~30%); the solver is the smallest fraction of the workflow.</div>
    </div>
  );
}

// ── Diagram 7: Finite Volume Discretisation ──────────────────────
function DiagramFVM() {
  const W = 900, H = 300;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Finite Volume Method — Cell-Centred Discretisation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="fvArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="fvArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
          <marker id="fvArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Left: cell stencil */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Finite Volume Cell Stencil  (2D)</text>

        {(()=>{
          const cellSize = 88;
          const gx = 90, gy = 50;  // grid top-left
          const labels = [
            ['NW','N','NE'],
            ['W', 'P', 'E'],
            ['SW','S','SE'],
          ];
          const colors = {
            P:'#FEF0F0', N:'#fff5e6', S:'#fff5e6', E:'#e8f4e8', W:'#e8f4e8',
            NW:'#fff', NE:'#fff', SW:'#fff', SE:'#fff',
          };
          const borderColors = {
            P:'#c0392b', N:'#e07b00', S:'#e07b00', E:'#27ae60', W:'#27ae60',
            NW:'#aaa', NE:'#aaa', SW:'#aaa', SE:'#aaa',
          };

          return <>
            {labels.map((row, ri) =>
              row.map((label, ci) => {
                const x = gx + ci * cellSize;
                const y = gy + ri * cellSize;
                return (
                  <g key={label}>
                    <rect x={x} y={y} width={cellSize} height={cellSize}
                      fill={colors[label] || '#fff'} stroke={borderColors[label] || '#aaa'} strokeWidth={label==='P'?2.5:1.5}/>
                    <text x={x+cellSize/2} y={y+cellSize/2+5}
                      textAnchor="middle" fontSize="14" fill={label==='P'?'#7B0D0D':'#555'} fontWeight={label==='P'?'700':'400'}>{label}</text>
                  </g>
                );
              })
            )}

            {/* Face labels e, w, n, s */}
            {[
              {x:gx+cellSize+cellSize/2, y:gy+cellSize+cellSize/2-4, label:'e', color:'#27ae60'},
              {x:gx+cellSize/2,          y:gy+cellSize+cellSize/2-4, label:'w', color:'#27ae60'},
              {x:gx+cellSize+cellSize/2, y:gy+cellSize-4,            label:'n', color:'#e07b00'},
              {x:gx+cellSize+cellSize/2, y:gy+2*cellSize-4,          label:'s', color:'#e07b00'},
            ].map(({x,y,label,color})=>(
              <text key={label} x={x} y={y} textAnchor="middle" fontSize="13" fill={color} fontWeight="700">{label}</text>
            ))}

            {/* Normal flux arrows out of P */}
            {/* East */}
            <line x1={gx+cellSize*2-4} y1={gy+cellSize+cellSize/2} x2={gx+cellSize*2+28} y2={gy+cellSize+cellSize/2}
              stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#fvArrG)"/>
            <text x={gx+cellSize*2+36} y={gy+cellSize+cellSize/2+4} fontSize="11" fill="#27ae60" fontWeight="700">F_e</text>
            {/* West */}
            <line x1={gx+cellSize+4} y1={gy+cellSize+cellSize/2} x2={gx+cellSize-28} y2={gy+cellSize+cellSize/2}
              stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#fvArrG)"/>
            <text x={gx+cellSize-36} y={gy+cellSize+cellSize/2+4} textAnchor="end" fontSize="11" fill="#27ae60" fontWeight="700">F_w</text>
            {/* North */}
            <line x1={gx+cellSize+cellSize/2} y1={gy+cellSize+4} x2={gx+cellSize+cellSize/2} y2={gy+cellSize-28}
              stroke="#e07b00" strokeWidth="2.5" markerEnd="url(#fvArr)"/>
            <text x={gx+cellSize+cellSize/2+6} y={gy+cellSize-32} fontSize="11" fill="#e07b00" fontWeight="700">F_n</text>
            {/* South */}
            <line x1={gx+cellSize+cellSize/2} y1={gy+2*cellSize-4} x2={gx+cellSize+cellSize/2} y2={gy+2*cellSize+28}
              stroke="#e07b00" strokeWidth="2.5" markerEnd="url(#fvArr)"/>
            <text x={gx+cellSize+cellSize/2+6} y={gy+2*cellSize+38} fontSize="11" fill="#e07b00" fontWeight="700">F_s</text>

            {/* Integral equation label */}
            <rect x={gx-68} y={gy+cellSize*3+8} width={435} height={32} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={gx-62} y={gy+cellSize*3+22} fontSize="11" fill="#7B0D0D" fontWeight="700">∫∫∫ ∂ρ/∂t dV + ∮ ρu·n̂ dA = ∮ μ∇u·n̂ dA + ∫∫∫ S dV</text>
            <text x={gx-62} y={gy+cellSize*3+36} fontSize="10" fill="#555">Transient     Convective flux         Diffusive flux        Source</text>
          </>;
        })()}

        {/* Right: convergence criteria + mesh types */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Mesh Types and y+ Requirements</text>

        {(()=>{
          const ox = PW+30, oy = 42;

          // Structured mesh sketch (left portion)
          const mw=120, mh=120;
          for(let i=0;i<=4;i++){
            const x=ox+i*(mw/4);
            // Slightly fan-shaped for visual interest
            const topY=oy+i*3, botY=oy+mh-i*3;
            // Vertical lines
            for(let j=0;j<=4;j++){
              const y=oy+j*(mh/4);
              if(j===0 || j===4){
                // horizontal sweep lines
              }
            }
          }
          // Just draw structured grid directly
          for(let i=0;i<=5;i++){
            const xL=ox+i*24;
          }

          return <>
            {/* Structured grid box */}
            <rect x={ox} y={oy} width={130} height={110} rx={2} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1.5"/>
            {/* Grid lines */}
            {[0,1,2,3,4,5].map(i=>(
              <g key={i}>
                <line x1={ox} y1={oy+i*22} x2={ox+130} y2={oy+i*22} stroke="#27ae60" strokeWidth="1" opacity="0.6"/>
                <line x1={ox+i*26} y1={oy} x2={ox+i*26} y2={oy+110} stroke="#27ae60" strokeWidth="1" opacity="0.6"/>
              </g>
            ))}
            <text x={ox+65} y={oy+125} textAnchor="middle" fontSize="11" fill="#145214" fontWeight="700">Structured (Hex)</text>
            <text x={ox+65} y={oy+139} textAnchor="middle" fontSize="10" fill="#444">Low diffusion</text>
            <text x={ox+65} y={oy+151} textAnchor="middle" fontSize="10" fill="#444">Hard to generate</text>

            {/* Unstructured grid box */}
            <rect x={ox+148} y={oy} width={130} height={110} rx={2} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
            {/* Triangular mesh approximation */}
            {[
              [ox+152,oy+8, ox+195,oy+32, ox+162,oy+55],
              [ox+195,oy+32,ox+230,oy+18, ox+240,oy+50],
              [ox+162,oy+55,ox+195,oy+32, ox+210,oy+72],
              [ox+210,oy+72,ox+240,oy+50, ox+258,oy+75],
              [ox+162,oy+55,ox+180,oy+88, ox+210,oy+72],
              [ox+154,oy+80,ox+180,oy+88, ox+170,oy+108],
              [ox+180,oy+88,ox+210,oy+72, ox+220,oy+100],
              [ox+240,oy+50,ox+258,oy+75, ox+272,oy+60],
            ].map((pts,i)=>(
              <polygon key={i} points={pts.map((_,j,a)=>j%2===0?`${a[j]},${a[j+1]}`:null).filter(Boolean).join(' ')}
                fill="none" stroke="#e07b00" strokeWidth="1.2" opacity="0.7"/>
            ))}
            <text x={ox+213} y={oy+125} textAnchor="middle" fontSize="11" fill="#8B4513" fontWeight="700">Unstructured (Tet/Tri)</text>
            <text x={ox+213} y={oy+139} textAnchor="middle" fontSize="10" fill="#444">Easy for complex geometry</text>
            <text x={ox+213} y={oy+151} textAnchor="middle" fontSize="10" fill="#444">More numerical diffusion</text>

            {/* y+ table */}
            <rect x={ox} y={oy+165} width={PW-30} height={112} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+6} y={oy+180} fontSize="12" fill="#7B0D0D" fontWeight="700">Wall y+ requirements:</text>
            {[
              {y:196, bg:'#FEF0F0', bc:'#c0392b', t:'Low-Re / near-wall: y+ ≈ 1',   s:'SST, k-ω — resolves viscous sublayer; ~20 cells in BL'},
              {y:215, bg:'#fff5e6', bc:'#e07b00', t:'Wall function: y+ = 30–300',    s:'Standard k-ε; BL not resolved — modelled; fewer cells'},
              {y:234, bg:'#e8f4e8', bc:'#27ae60', t:'LES: y+ ≈ 1 and Δx+,Δz+ < 50', s:'Resolve viscous + log-law; very fine wall mesh required'},
              {y:253, bg:'#f5e6ff', bc:'#9b59b6', t:'Grid independence: GCI < 3%',   s:'Run 3 grids; Richardson extrapolation; report uncertainty'},
            ].map(({y,bg,bc,t,s})=>(
              <g key={y}>
                <rect x={ox+2} y={oy+y-12} width={PW-34} height={17} rx={2} fill={bg} stroke={bc} strokeWidth="0.8"/>
                <text x={ox+8} y={oy+y} fontSize="10" fill="#333" fontWeight="700">{t}</text>
                <text x={ox+8} y={oy+y+10} fontSize="9"  fill="#666">{s}</text>
              </g>
            ))}
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>The finite volume method (FVM) divides the domain into control volumes and integrates the governing equations (conservation of mass, momentum, energy) over each cell. The result is a balance equation: the net flux of a quantity through all cell faces equals the source within the cell. Face fluxes are approximated from neighbouring cell-centre values using interpolation schemes (upwind, central, QUICK), making the scheme conservative by construction. Mesh quality — cell orthogonality, aspect ratio, and wall-normal resolution (y+) — critically controls both accuracy and convergence. Structured hexahedral meshes give lowest numerical diffusion but are difficult to generate for complex geometries; unstructured tetrahedral or polyhedral meshes are easier to create but require finer resolution for equivalent accuracy.</div>
    </div>
  );
}

// ── Diagram 8: Multiphase Flow Regimes ───────────────────────────
function DiagramMultiphaseRegimes() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Multiphase Flow — Gas-Liquid Flow Regimes in a Horizontal Pipe</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="mpArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#555"/>
          </marker>
          <pattern id="liquidPat" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="8" x2="8" y2="0" stroke="#aed6f1" strokeWidth="1.2" opacity="0.7"/>
          </pattern>
          <pattern id="gasPat" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
            <circle cx="3" cy="3" r="1" fill="#f0cece" opacity="0.5"/>
          </pattern>
        </defs>

        {/* Regime headers */}
        <text x={450} y={18} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">← Increasing gas fraction  /  Increasing gas velocity →</text>

        {/* Six regimes side by side */}
        {[
          {
            x:15, label:'Bubbly',
            desc:'Gas bubbles\nin liquid',
            draw: (x,cy,pw,ph) => {
              // liquid fill
              const rects = [{x,y:cy-ph/2,w:pw,h:ph,fill:'url(#liquidPat)',stroke:'#aed6f1'}];
              const bubbles = [{bx:x+pw*0.25,by:cy-10,r:8},{bx:x+pw*0.6,by:cy+8,r:6},{bx:x+pw*0.45,by:cy-18,r:5},{bx:x+pw*0.75,by:cy,r:7}];
              return {rects, bubbles, gasTop:false};
            }
          },
          {
            x:162, label:'Plug',
            desc:'Elongated gas\nplugs',
            draw: (x,cy,pw,ph) => ({rects:[{x,y:cy-ph/2,w:pw,h:ph,fill:'url(#liquidPat)',stroke:'#aed6f1'}], gasTop:false, plugs:[{x:x+pw*0.1,y:cy-ph*0.38,w:pw*0.35,h:ph*0.76,rx:12}]}),
          },
          {
            x:309, label:'Stratified',
            desc:'Smooth gas-\nliquid interface',
            draw: (x,cy,pw,ph) => ({liquidTop:false,gasTop:true,splitY:cy-ph*0.1}),
          },
          {
            x:456, label:'Wavy',
            desc:'Gas faster,\nwavy interface',
            draw: (x,cy,pw,ph) => ({liquidTop:false,gasTop:true,splitY:cy-ph*0.1,wavy:true}),
          },
          {
            x:603, label:'Slug',
            desc:'Alternating liquid\nand gas slugs',
            draw: (x,cy,pw,ph) => ({slug:true}),
          },
          {
            x:750, label:'Annular',
            desc:'Liquid film on\nwall, gas core',
            draw: (x,cy,pw,ph) => ({annular:true}),
          },
        ].map(({ x, label, desc }, regIdx) => {
          const pw = 130, ph = 130, cy = 170;
          const colors = ['#2980b9','#2980b9','#9b59b6','#9b59b6','#c0392b','#e07b00'];
          const color = colors[regIdx];

          return (
            <g key={label}>
              {/* Pipe outline */}
              <rect x={x} y={cy-ph/2} width={pw} height={ph} rx={4} fill="#f0f0f0" stroke="#555" strokeWidth="2"/>

              {/* Regime-specific fill */}
              {regIdx === 0 && <>
                <rect x={x+1} y={cy-ph/2+1} width={pw-2} height={ph-2} rx={3} fill="url(#liquidPat)" opacity="0.8"/>
                {[[x+pw*0.2,cy-12,9],[x+pw*0.55,cy+10,7],[x+pw*0.4,cy-22,6],[x+pw*0.75,cy+2,8],[x+pw*0.3,cy+16,5]].map(([bx,by,r],i)=>(
                  <circle key={i} cx={bx} cy={by} r={r} fill="#fff" stroke="#2980b9" strokeWidth="1.5" opacity="0.9"/>
                ))}
              </>}
              {regIdx === 1 && <>
                <rect x={x+1} y={cy-ph/2+1} width={pw-2} height={ph-2} rx={3} fill="url(#liquidPat)" opacity="0.8"/>
                <rect x={x+pw*0.12} y={cy-ph*0.35} width={pw*0.35} height={ph*0.7} rx={14} fill="#fff" stroke="#2980b9" strokeWidth="1.5" opacity="0.9"/>
              </>}
              {(regIdx === 2 || regIdx === 3) && <>
                <rect x={x+1} y={cy} width={pw-2} height={ph/2-1} rx={2} fill="url(#liquidPat)" opacity="0.8"/>
                <rect x={x+1} y={cy-ph/2+1} width={pw-2} height={ph/2} rx={2} fill="url(#gasPat)" opacity="0.5"/>
                {regIdx === 3 && [0,1,2,3,4].map(i=>(
                  <path key={i} d={`M ${x+i*pw/4},${cy} C ${x+i*pw/4+pw/8},${cy-12} ${x+i*pw/4+pw/6},${cy-8} ${x+(i+1)*pw/4},${cy}`}
                    fill="none" stroke="#9b59b6" strokeWidth="1.5"/>
                ))}
                <line x1={x} y1={cy} x2={x+pw} y2={cy} stroke="#9b59b6" strokeWidth={regIdx===2?2:1.5} strokeDasharray={regIdx===3?"4,2":"none"}/>
              </>}
              {regIdx === 4 && <>
                <rect x={x+1} y={cy-ph/2+1} width={pw-2} height={ph-2} rx={3} fill="url(#liquidPat)" opacity="0.8"/>
                <rect x={x+pw*0.05} y={cy-ph/2+4} width={pw*0.38} height={ph-8} rx={3} fill="#fff" stroke="#c0392b" strokeWidth="1.5" opacity="0.9"/>
                <rect x={x+pw*0.55} y={cy-ph/2+4} width={pw*0.38} height={ph-8} rx={3} fill="#fff" stroke="#c0392b" strokeWidth="1.5" opacity="0.9"/>
              </>}
              {regIdx === 5 && <>
                <rect x={x+1} y={cy-ph/2+1} width={pw-2} height={ph-2} rx={3} fill="#fff" opacity="0.8"/>
                {/* Gas core */}
                <rect x={x+pw*0.18} y={cy-ph*0.35} width={pw*0.64} height={ph*0.7} rx={3} fill="url(#gasPat)" opacity="0.6"/>
                {/* Liquid film top/bottom */}
                <rect x={x+1}     y={cy-ph/2+1}       width={pw-2} height={ph*0.14} rx={3} fill="url(#liquidPat)" opacity="0.9"/>
                <rect x={x+1}     y={cy+ph*0.36}      width={pw-2} height={ph*0.14} rx={3} fill="url(#liquidPat)" opacity="0.9"/>
              </>}

              {/* Label */}
              <text x={x+pw/2} y={cy+ph/2+18} textAnchor="middle" fontSize="13" fill={color} fontWeight="700">{label}</text>
              {desc.split('\n').map((l, j) => (
                <text key={j} x={x+pw/2} y={cy+ph/2+33+j*13} textAnchor="middle" fontSize="10" fill="#555">{l}</text>
              ))}
            </g>
          );
        })}

        {/* Arrows */}
        <line x1={15} y1={290} x2={885} y2={290} stroke="#555" strokeWidth="2" markerEnd="url(#mpArr)"/>
        <text x={100} y={306} fontSize="11" fill="#2980b9">Low void fraction</text>
        <text x={700} y={306} textAnchor="middle" fontSize="11" fill="#c0392b">High void fraction</text>
      </svg>
      <div style={S.dCaption}>Gas-liquid flow in pipes exhibits a rich variety of flow patterns (regimes) depending on the superficial velocities and volume fractions of each phase. At low gas fractions, discrete bubbles are suspended in liquid (bubbly flow). As gas increases, bubbles coalesce into elongated plugs/slugs. At moderate gas fractions, stratification occurs by gravity (stratified smooth, then wavy). At high gas velocities, intermittent liquid slugs alternate with gas pockets (slug flow — the most complex and damaging regime for pipelines). At very high gas fractions the liquid forms a thin wall film while gas occupies the core (annular flow). Accurate prediction of the prevailing regime is critical for oil-gas pipeline design, nuclear reactor safety, and boiling heat exchangers.</div>
    </div>
  );
}

// ── Diagram 9: Multiphase Modelling Approaches ───────────────────
function DiagramMultiphaseModels() {
  const W = 900, H = 290;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Multiphase Flow Modelling — Euler-Euler, Euler-Lagrange and VOF</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Three approach boxes */}
        {[
          {
            x:10, w:270, label:'Euler-Euler (Two-Fluid)',
            fill:'#FEF0F0', stroke:'#c0392b', tc:'#7B0D0D',
            points:[
              'Each phase has own velocity & T field',
              'Volume fraction αk (Σαk = 1)',
              'Interphasic exchange terms: drag,',
              '  heat, mass transfer',
              'Phase equations coupled via pressure',
              '',
              'Use when: dispersed flow, fluidised',
              '  beds, bubbly pipe flow',
              'Cost: ~2× single-phase',
            ],
          },
          {
            x:310, w:270, label:'Euler-Lagrange (DPM)',
            fill:'#e8f4e8', stroke:'#27ae60', tc:'#145214',
            points:[
              'Continuous phase: RANS/LES (Euler)',
              'Dispersed phase: particle trajectories',
              'Newton\'s law for each parcel:',
              '  m du_p/dt = F_drag + F_gravity + ...',
              'F_drag = 3μ(u-u_p)/(ρ_p d_p²) Cd Re_p',
              '',
              'Use when: dilute spray, particle-laden',
              '  flow, pneumatic conveying (<10% vol)',
              'Cost: scales with particle number',
            ],
          },
          {
            x:610, w:280, label:'Volume of Fluid (VOF)',
            fill:'#fff5e6', stroke:'#e07b00', tc:'#8B4513',
            points:[
              'Single set of NS equations',
              'Phase indicator F (0=gas, 1=liquid)',
              '∂F/∂t + u·∇F = 0  (advection eq.)',
              'Interface reconstructed per cell',
              'PLIC: piecewise-linear interface',
              'Surface tension: CSF model',
              '  F_σ = σκ∇F',
              'Use when: free-surface, sloshing,',
              '  wave breaking, jet breakup',
            ],
          },
        ].map(({ x, w, label, fill, stroke, tc, points }) => (
          <g key={label}>
            <rect x={x} y={10} width={w} height={270} rx={5} fill={fill} stroke={stroke} strokeWidth="2"/>
            <text x={x+w/2} y={30} textAnchor="middle" fontSize="13" fill={tc} fontWeight="700">{label}</text>
            <line x1={x+8} y1={36} x2={x+w-8} y2={36} stroke={stroke} strokeWidth="1"/>
            {points.map((pt, i) => (
              <text key={i} x={x+10} y={54+i*17} fontSize="10.5" fill="#333" fontFamily="'Courier New',monospace">{pt}</text>
            ))}
          </g>
        ))}

        {/* Bottom comparison strip */}
        <rect x={10} y={255} width={560} height={30} rx={3} fill="none"/>
        {/* Accuracy arrow spanning all three */}
        <line x1={10} y1={282} x2={890} y2={282} stroke="#9b59b6" strokeWidth="1.8"/>
        <text x={450} y={278} textAnchor="middle" fontSize="11" fill="#9b59b6" fontWeight="700">Interface sharpness: VOF (sharp) &gt; Euler-Lagrange (parametric) &gt; Euler-Euler (diffuse)</text>
      </svg>
      <div style={S.dCaption}>Three principal CFD approaches model multiphase flows. The Euler-Euler (two-fluid) method solves separate conservation equations for each phase, coupled by interphase exchange terms — suitable for dense dispersed flows but requires closure models for drag, lift, and turbulence. The Euler-Lagrange (DPM — Discrete Phase Model) tracks individual particle/droplet trajectories through a Eulerian background flow field — accurate for dilute dispersed phases but prohibitively expensive above ~10% volume fraction. The Volume of Fluid (VOF) method tracks a single phase-fraction field F advected with the flow; the interface is geometrically reconstructed each step — ideal for sharp free-surface flows (waves, jets, sloshing) but requires very fine mesh at the interface and is expensive for numerous small bubbles or droplets.</div>
    </div>
  );
}

// ── Diagram 10: Surface Tension & Interface Physics ──────────────
function DiagramSurfaceTension() {
  const W = 900, H = 290;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Surface Tension, Capillary Effects and Droplet Dynamics</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="stArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="stArrB" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#2980b9"/>
          </marker>
        </defs>

        {/* Left: Capillary rise */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Capillary Rise and Contact Angle</text>

        {(()=>{
          const cx = 200, tubeW = 50, tubeH = 180, liquidLevel = 230;
          return <>
            {/* Reservoir */}
            <rect x={60} y={liquidLevel} width={280} height={50} rx={3} fill="#aed6f1" stroke="#2980b9" strokeWidth="1.5" opacity="0.5"/>
            <line x1={60} y1={liquidLevel} x2={340} y2={liquidLevel} stroke="#2980b9" strokeWidth="2"/>
            <text x={200} y={liquidLevel+30} textAnchor="middle" fontSize="11" fill="#2980b9">Liquid reservoir</text>

            {/* Capillary tube */}
            <line x1={cx-tubeW/2} y1={30} x2={cx-tubeW/2} y2={liquidLevel} stroke="#555" strokeWidth="3"/>
            <line x1={cx+tubeW/2} y1={30} x2={cx+tubeW/2} y2={liquidLevel} stroke="#555" strokeWidth="3"/>

            {/* Liquid rise inside tube */}
            {(()=>{
              const riseTop = 90; // where liquid ends inside tube
              return <>
                {/* Liquid column */}
                <rect x={cx-tubeW/2+3} y={riseTop+12} width={tubeW-6} height={liquidLevel-riseTop-12}
                  fill="#aed6f1" opacity="0.7"/>
                {/* Meniscus (concave for wetting fluid) */}
                <path d={`M ${cx-tubeW/2+3},${riseTop+12} Q ${cx},${riseTop-4} ${cx+tubeW/2-3},${riseTop+12}`}
                  fill="#aed6f1" stroke="#2980b9" strokeWidth="2" opacity="0.9"/>

                {/* Contact angle arc */}
                <path d={`M ${cx-tubeW/2+3},${riseTop+22} A 18 18 0 0 1 ${cx-tubeW/2+18},${riseTop+12}`}
                  fill="none" stroke="#e07b00" strokeWidth="1.8"/>
                <text x={cx-tubeW/2-14} y={riseTop+28} textAnchor="end" fontSize="12" fill="#e07b00" fontWeight="700">θ</text>
                <text x={cx-tubeW/2-14} y={riseTop+42} textAnchor="end" fontSize="10" fill="#e07b00">contact</text>
                <text x={cx-tubeW/2-14} y={riseTop+54} textAnchor="end" fontSize="10" fill="#e07b00">angle</text>

                {/* Rise height h */}
                <line x1={cx+tubeW/2+16} y1={liquidLevel} x2={cx+tubeW/2+16} y2={riseTop+12}
                  stroke="#c0392b" strokeWidth="1.8" markerEnd="url(#stArr)" markerStart="url(#stArr)"/>
                <text x={cx+tubeW/2+24} y={(liquidLevel+riseTop+12)/2+4} fontSize="12" fill="#c0392b" fontWeight="700">h</text>

                {/* Surface tension arrows at contact line */}
                <line x1={cx-tubeW/2+3} y1={riseTop+12} x2={cx-tubeW/2+3-18} y2={riseTop+12-22}
                  stroke="#27ae60" strokeWidth="2" markerEnd="url(#stArr)"/>
                <text x={cx-tubeW/2-22} y={riseTop-14} textAnchor="end" fontSize="11" fill="#27ae60" fontWeight="700">σ</text>
              </>;
            })()}

            {/* Formulas */}
            <rect x={18} y={210} width={PW-26} height={64} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={26} y={226} fontSize="12" fill="#7B0D0D" fontWeight="700">Jurin's law:  h = 2σ cosθ / (ρgr)</text>
            <text x={26} y={242} fontSize="11" fill="#444">r = tube radius;  θ &lt; 90°: wetting (water-glass)</text>
            <text x={26} y={258} fontSize="11" fill="#444">Young-Dupré: σ_SG = σ_SL + σ_LG cosθ</text>
            <text x={26} y={272} fontSize="11" fill="#444">Weber: We = ρU²L/σ  (inertia/surface tension)</text>
          </>;
        })()}

        {/* Right: Droplet dynamics */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Droplet Deformation and Breakup</text>

        {(()=>{
          const ox=PW+35;
          const droplets = [
            {cx:ox+60, cy:150, rx:28, ry:28, label:'Spherical\nWe<<1', color:'#2980b9'},
            {cx:ox+160, cy:150, rx:38, ry:22, label:'Oblate\nWe~1', color:'#9b59b6'},
            {cx:ox+270, cy:150, rx:52, ry:16, label:'Disc\nWe~5', color:'#e07b00'},
            {cx:ox+380, cy:150, rx:62, ry:10, label:'Bag breakup\nWe~10', color:'#c0392b'},
          ];

          return <>
            {droplets.map(({cx,cy,rx,ry,label,color},i)=>(
              <g key={i}>
                <ellipse cx={cx} cy={cy} rx={rx} ry={ry}
                  fill={color} opacity="0.25" stroke={color} strokeWidth="2"/>
                {/* Arrow showing flow direction */}
                {i>0 && <line x1={cx-rx-22} y1={cy} x2={cx-rx-4} y2={cy}
                  stroke="#555" strokeWidth="1.5" markerEnd="url(#stArr)"/>}
                {label.split('\n').map((l,j)=>(
                  <text key={j} x={cx} y={cy+ry+16+j*14} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">{l}</text>
                ))}
              </g>
            ))}
            {/* Breakup products */}
            {[[ox+390, 138],[ox+410,155],[ox+375,162]].map(([bx,by],i)=>(
              <circle key={i} cx={bx} cy={by} r={4+i*2} fill="#c0392b" opacity="0.5"/>
            ))}
            <text x={ox+400} y={180} textAnchor="middle" fontSize="10" fill="#c0392b">fragments</text>

            {/* We axis */}
            <line x1={ox+30} y1={220} x2={ox+440} y2={220} stroke="#555" strokeWidth="1.5" markerEnd="url(#stArr)"/>
            <text x={ox+450} y={224} fontSize="11" fill="#555" fontWeight="700">We</text>
            {[['ox+60','0.1'],['ox+160','1'],['ox+270','5'],['ox+380','10']].map(([xExpr,v],i)=>(
              <text key={i} x={ox+[60,160,270,380][i]} y={234} textAnchor="middle" fontSize="10" fill="#555">{v}</text>
            ))}

            {/* Dimensionless numbers box */}
            <rect x={ox} y={242} width={PW-30} height={36} rx={3} fill="#FEF0F0" stroke="#f0cece"/>
            <text x={ox+6} y={256} fontSize="11" fill="#7B0D0D" fontWeight="700">We = ρU²d/σ  (breakup);  Bo = ρgL²/σ  (gravity vs surface);  Oh = μ/√(ρσL)  (viscosity vs σ)</text>
            <text x={ox+6} y={270} fontSize="10" fill="#555">Ohnesorge: Oh &lt; 0.1 → breakup dominated by We;  Oh &gt; 1 → viscosity stabilises droplet</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Surface tension σ (N/m) arises from unbalanced intermolecular forces at a fluid interface and acts to minimise surface area. Capillary rise height h = 2σcosθ/(ρgr) demonstrates the balance between surface tension and gravity; the contact angle θ characterises the wettability of the solid. Droplet dynamics are governed by the Weber number We = ρU²d/σ: at low We surface tension maintains a spherical shape; increasing We deforms the droplet through oblate, disc, and bag modes until breakup occurs at We ~ 10–12 (critical Weber). The Ohnesorge number Oh = μ/√(ρσL) measures viscous damping of surface-tension-driven oscillations. These phenomena govern spray atomisation, inkjet printing, rain erosion, and boiling heat transfer.</div>
    </div>
  );
}

// ── Diagram 11: Potential Flow — Stream Function Map ─────────────
function DiagramStreamFunction() {
  const W = 900, H = 290;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Stream Function and Potential Function — Orthogonal Network</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="sfArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#2980b9"/>
          </marker>
          <marker id="sfArrR" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
        </defs>

        {/* Left panel: uniform flow over a corner */}
        <rect x={10} y={10} width={390} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={205} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Flow in a Corner  (ψ = A r^n sin nθ)</text>

        {(()=>{
          const ox=20, oy=30, pw=370, ph=230;
          const cx=ox, cy=oy+ph; // corner at bottom-left
          // Streamlines: ψ = r^2 sin(2θ) → xy = const
          const psiVals = [0.3, 0.8, 1.6, 3.0, 5.5, 9.0];
          return <>
            {/* Walls */}
            <line x1={cx} y1={cy-ph} x2={cx} y2={cy} stroke="#555" strokeWidth="3"/>
            <line x1={cx} y1={cy} x2={cx+pw} y2={cy} stroke="#555" strokeWidth="3"/>
            <text x={cx-12} y={cy-ph/2} textAnchor="end" fontSize="11" fill="#555">Wall</text>
            <text x={cx+pw/2} y={cy+16} textAnchor="middle" fontSize="11" fill="#555">Wall</text>

            {/* Streamlines ψ = const → x*y = C */}
            {psiVals.map((C,i)=>{
              const pts=[];
              for(let x=1;x<=pw-2;x+=2){
                const y=C*10000/x;
                if(y>0 && y<ph) pts.push({x:cx+x, y:cy-y});
              }
              const path=pts.map(({x,y},j)=>`${j===0?'M':'L'} ${x},${y}`).join(' ');
              return <path key={i} d={path} fill="none" stroke="#2980b9" strokeWidth="1.5" opacity={0.5+i*0.07}/>;
            })}

            {/* Potential lines φ = const → x²-y² = const */}
            {[-5,-2,0,2,5,10,16].map((C,i)=>{
              // x²-y² = C → y = sqrt(x²-C) for x²>C
              const pts=[];
              for(let x=0;x<=pw;x+=2){
                const y2=x*x/100-C*15;
                if(y2>=0){
                  const y=Math.sqrt(y2);
                  if(y>=0 && y<ph) pts.push({x:cx+x,y:cy-y});
                }
              }
              const path=pts.map(({x,y},j)=>`${j===0?'M':'L'} ${x},${y}`).join(' ');
              return <path key={i} d={path} fill="none" stroke="#c0392b" strokeWidth="1.2" strokeDasharray="5,3" opacity="0.55"/>;
            })}

            {/* Labels */}
            <text x={cx+180} y={cy-140} fontSize="12" fill="#2980b9" fontWeight="700">ψ = const</text>
            <text x={cx+180} y={cy-126} fontSize="11" fill="#2980b9">(streamlines)</text>
            <text x={cx+280} y={cy-90} fontSize="12" fill="#c0392b" fontWeight="700">φ = const</text>
            <text x={cx+280} y={cy-76} fontSize="11" fill="#c0392b">(equipotentials)</text>
            <text x={cx+150} y={cy-30} fontSize="11" fill="#555">ψ and φ always perpendicular</text>

            {/* Right-angle marker at intersection */}
            <rect x={cx+110} y={cy-58} width={8} height={8} fill="none" stroke="#e07b00" strokeWidth="1.5"/>
          </>;
        })()}

        {/* Right panel: key relationships */}
        <rect x={420} y={10} width={470} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={655} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Stream Function and Velocity Potential — Key Relations</text>

        {[
          {y:46,  bg:'#FEF0F0', bc:'#c0392b', title:'Velocity potential φ  (irrotational flow)',
           lines:['u = ∂φ/∂x,  v = ∂φ/∂y  (2D Cartesian)','∇²φ = 0  (Laplace — flows, electrostatics, heat)']},
          {y:96,  bg:'#e8f4e8', bc:'#27ae60', title:'Stream function ψ  (incompressible flow)',
           lines:['u = ∂ψ/∂y,  v = −∂ψ/∂x','∇²ψ = −ω  (vorticity source; ω=0 → ∇²ψ=0)']},
          {y:146, bg:'#fff5e6', bc:'#e07b00', title:'Cauchy-Riemann equations (complex potential)',
           lines:['∂φ/∂x = ∂ψ/∂y  and  ∂φ/∂y = −∂ψ/∂x','w(z) = φ + iψ  →  dw/dz = u − iv  (complex velocity)']},
          {y:196, bg:'#f5e6ff', bc:'#9b59b6', title:'Flow rate between streamlines',
           lines:['Q = ψ₂ − ψ₁  (volume flow per unit depth between ψ₁ and ψ₂)','Streamlines are equally spaced in equal-flow channels']},
          {y:246, bg:'#fff5f5', bc:'#c0392b', title:'Bernoulli on a streamline (inviscid, steady)',
           lines:['p/ρ + u²/2 + gz = const  along ψ = const','High speed (close streamlines) → low p  (Bernoulli)']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={y}>
            <rect x={428} y={y} width={454} height={44} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
            <text x={434} y={y+14} fontSize="11" fill="#333" fontWeight="700">{title}</text>
            {lines.map((l,i)=>(
              <text key={i} x={434} y={y+28+i*13} fontSize="10.5" fill="#444" fontFamily="'Courier New',monospace">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The stream function ψ and velocity potential φ are the two scalar fields that fully characterise 2D incompressible irrotational flow. Lines of constant ψ are streamlines; lines of constant φ are equipotential lines — and because the Cauchy-Riemann conditions link them, the two families are always perpendicular. Together they form an orthogonal flow net that can be constructed graphically or analytically. The complex potential w(z) = φ + iψ maps every 2D potential flow to an analytic function of the complex variable z — enabling powerful techniques including conformal mapping (Joukowski), method of images, and circle theorem to solve otherwise intractable boundary problems.</div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────
export default function AdvancedFluidDynamics() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Advanced Topics in Fluid Dynamics</h1>
      <p style={S.p}>
        Beyond the fundamentals of laminar flow, simple boundary layers, and inviscid theory lies a set of topics that define the frontier of both research and engineering practice. Turbulence — the chaotic, multi-scale state that characterises almost all high-Reynolds-number flows — remains one of the great unsolved problems of classical physics. Potential flow theory, though limited to inviscid irrotational conditions, provides exact analytical solutions and the foundation of panel methods. Computational Fluid Dynamics (CFD) has become the third pillar of fluid mechanics alongside theory and experiment, enabling simulation of flows too complex for analytical treatment. And multiphase flows — involving two or more immiscible phases, free surfaces, or phase change — govern phenomena from ocean waves to nuclear reactors to inkjet printing. This page covers the essential physics, mathematics, and modelling approaches for each of these advanced topics.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Turbulence: energy injected at large scales cascades to small scales; E(k) ∝ k^(-5/3) in the inertial subrange (Kolmogorov 1941)",
        "Reynolds decomposition: u = Ū + u′; RANS equations contain unclosed Reynolds stress −ρ⭨u′ᵢu′ⱼ⭩ — the closure problem",
        "k-ε: robust workhorse; k-ω SST: more accurate in adverse pressure gradients — industry standard for aerodynamics",
        "Potential flow: irrotational + incompressible → ∇²φ = 0 (Laplace); solutions superpose linearly",
        "Joukowski transform: conformal map from cylinder to aerofoil; Kutta condition fixes Γ → lift L = ρU∞Γ",
        "CFD: geometry → mesh → physics → solver → post-processing; y+ and grid independence are critical",
        "FVM: integrate conservation laws over control volumes; cell fluxes ensure conservativeness",
        "Multiphase regimes: bubbly, plug, stratified, wavy, slug, annular — depend on void fraction and velocities",
        "Euler-Euler, Euler-Lagrange (DPM), VOF: three paradigms for multiphase CFD, each suited to different conditions",
        "Surface tension: We = ρU²d/σ governs droplet breakup; capillary rise h = 2σcosθ/(ρgr)",
      ]}/>

      <h2 style={S.h2}>Turbulence — The Energy Cascade and Kolmogorov Theory</h2>
      <p style={S.p}>
        Turbulence is the irregular, three-dimensional, time-dependent motion that arises at high Reynolds numbers when inertial forces overwhelm viscous damping. It is characterised by a broad range of length and time scales — from the large energy-containing eddies (comparable to the flow geometry) down to the tiny Kolmogorov microscale (where viscosity finally dissipates energy as heat). Kolmogorov's 1941 theory provides a statistical description: in the inertial subrange, far from both production and dissipation, the energy spectrum depends only on wavenumber k and dissipation rate ε.
      </p>
      <DiagramEnergyCascade/>
      <Table2 rows={[
        ["Turbulent kinetic energy","k = ½⭨u′ᵢu′ᵢ⭩ = ½(u′² + v′² + w′²)  (J/kg)\nMeasure of turbulence intensity; distributed across all scales"],
        ["Dissipation rate ε","ε = ν⭨(∂u′ᵢ/∂xⱼ)²⭩  (W/kg = m²/s³)\nRate at which TKE → heat; ε sets the cascade rate at all scales"],
        ["Kolmogorov length scale","η = (ν³/ε)^(1/4)\nSmallest dynamically active scale; viscosity dissipates here\nFor boundary layer: η/L ~ Re^(-3/4)"],
        ["Kolmogorov time scale","τ_η = (ν/ε)^(1/2)\nTurnover time of smallest eddies"],
        ["Kolmogorov velocity scale","v_η = (νε)^(1/4)\nRe based on Kolmogorov scale = v_η η/ν = 1 always"],
        ["Inertial subrange","l_EI ≫ l ≫ η:  E(k) = C_K ε^(2/3) k^(-5/3)\nC_K ≈ 1.5 (Kolmogorov constant);  universal, Re-independent"],
        ["Turbulence intensity","Tu = √(u′²rms) / Ū  (typically 1–20%)\nMeasured by hot-wire anemometry; affects transition and heat transfer"],
        ["DNS cost","N_grid ~ (L/η)^3 ~ Re^(9/4) per dimension\nRe = 10⁴ requires ~10^10 grid points — feasible only on largest supercomputers"],
      ]}/>

      <h2 style={S.h2}>Reynolds-Averaged Navier–Stokes (RANS) and Turbulence Models</h2>
      <p style={S.p}>
        The Reynolds-Averaged Navier–Stokes equations are obtained by time-averaging the instantaneous Navier–Stokes equations after substituting the Reynolds decomposition. The resulting equations are identical in form to the original, except for the appearance of the Reynolds stress tensor −ρ⭨u′ᵢu′ⱼ⭩ — six independent unknowns that must be modelled. This is the closure problem: there are more unknowns than equations. Every turbulence model is an attempt to provide this closure, trading accuracy for computational economy.
      </p>
      <DiagramRANS/>
      <Table2 rows={[
        ["Reynolds decomposition","u_i = Ū_i + u′_i;  ⭨u′_i⭩ = 0 by definition\nApplied to all quantities: velocity, pressure, temperature, concentration"],
        ["Reynolds stress","τ_ij = −ρ⭨u′_iu′_j⭩  (Pa)\nSymmetric 3×3 tensor — 6 independent components\nRepresents momentum transport by turbulent fluctuations"],
        ["Boussinesq hypothesis","τ_ij = μ_t(∂Ū_i/∂x_j + ∂Ū_j/∂x_i) − (2/3)ρk δ_ij\nTurbulent viscosity μ_t analogous to molecular μ\nPoor for swirling, strongly 3D, or buoyancy-driven flows"],
        ["Mixing-length model","μ_t = ρ l_m² |∂Ū/∂y|\nPrandtl (1925); l_m = κy (BL), κ = 0.41 (von Kármán)\nSimple, no transport equations; BL only"],
        ["Spalart-Allmaras","One-equation model for ν̃_t; very popular in aerodynamics\nDeveloped specifically for attached airfoil BL flows"],
        ["k-ε model","Two equations for k and ε; closed via μ_t = Cμρk²/ε\nRobust, well-calibrated; poor for APG, curved surfaces, rotation"],
        ["k-ω SST","Blends k-ω (wall) and k-ε (free stream); adds stress limiter\nMost widely used model in aerospace and turbomachinery today"],
        ["Reynolds Stress Model","Six transport equations for ⭨u′ᵢu′ⱼ⭩; no Boussinesq assumption\nCaptures anisotropy, rotation, streamline curvature; expensive; complex to converge"],
      ]}/>

      <h2 style={S.h2}>k-ε and k-ω SST in Detail</h2>
      <DiagramTurbulenceModels/>
      <Table2 rows={[
        ["Standard k-ε constants","Cμ=0.09, Cε1=1.44, Cε2=1.92, σk=1.0, σε=1.3\nDerived from DNS data and experiment for simple shear flows"],
        ["Production term Pk","Pk = μ_t S²  (S = √(2S_ij S_ij) = strain rate magnitude)\nPk = μ_t (∂Ū_i/∂x_j)(∂Ū_i/∂x_j + ∂Ū_j/∂x_i)"],
        ["Wall functions","Log-law: Ū+ = (1/κ)ln(y+) + B  (κ=0.41, B=5.2)\nAvoid resolving viscous sublayer;  valid for y+ = 30–300\nSignificant error in separated regions"],
        ["Enhanced wall treatment","Two-layer approach: resolve viscous sublayer (y+~1) in inner layer\nBlend with log-law outer layer;  more accurate but more cells"],
        ["SST blending","F1 = tanh(arg1⁴);  activates k-ω near wall (y<δ)\nF2 controls stress limiter:  νt = a1k/max(a1ω, SF2)"],
        ["Transition SST (γ-Reθ)","Adds transport equations for intermittency γ and momentum-thickness Re\nPredicts laminar-turbulent transition location on airfoils and blades"],
        ["LES sub-grid scale","Smagorinsky: τ_SGS = −2C_s²Δ²|S̃|S̃_ij\nC_s ≈ 0.1–0.2; Δ = filter width (cell size);  dynamic: C_s computed locally"],
        ["WMLES","Wall-modelled LES: RANS near wall, LES away — practical at high Re\nReduces cost by ~Re^{0.4} vs full LES"],
      ]}/>

      <h2 style={S.h2}>Potential Flow Theory</h2>
      <p style={S.p}>
        Potential flow theory assumes the fluid is inviscid and the flow is irrotational (∇×u = 0). Together with incompressibility these conditions reduce the governing equations to Laplace's equation ∇²φ = 0 — a linear elliptic PDE with a vast library of analytical solutions. Because the equation is linear, any two solutions can be superposed to create new solutions. The five elementary building blocks — uniform flow, source/sink, free vortex, doublet, and combinations thereof — allow exact analytical treatment of flows around circles, cylinders, Rankine ovals, and, via conformal mapping, arbitrary aerofoil shapes.
      </p>
      <DiagramPotentialFlow/>
      <Table2 rows={[
        ["Laplace equation","∇²φ = 0  (irrotational incompressible flow)\nAlso governs electrostatics, steady heat conduction, groundwater flow\nGreen's functions and boundary element methods apply"],
        ["Source strength m","m = Q/(2π)  (2D);  Q = volume flow per unit depth (m²/s)\nSink: m < 0;  point source in 3D: φ = −m/(4πr)"],
        ["Free vortex circulation","Γ = ∮ u·dl  (around a closed curve enclosing the vortex core)\nIrrotational everywhere except at r=0;  V_θ = Γ/(2πr)"],
        ["Doublet strength κ","κ = source strength × separation distance, as both → 0\nProduces streamlines that are circles; used to model cylinders"],
        ["Superposition examples","Uniform + source: Rankine half-body (blunt nose)\nUniform + doublet: cylinder with zero drag (d'Alembert)\nUniform + doublet + vortex: cylinder with lift (Joukowski, L=ρU∞Γ)"],
        ["D'Alembert's paradox","Potential flow around any body in steady uniform flow → zero drag\nPressure distribution fore-aft symmetric → net force = 0\nResolved by including viscous wake (boundary layer separation)"],
        ["Method of images","Mirror charges/sources in a wall satisfy the no-penetration BC\nSource near wall: add equal source on other side of wall\nUsed in aerofoil near ground effect, vortex dynamics"],
        ["Panel methods","Distribute sources, vortices, or doublets on body surface\nSolve linear system for strengths;  fast, accurate for attached flow\nUsed in XFOIL, VSAERO — standard low-fidelity aerodynamic design tool"],
      ]}/>

      <h2 style={S.h2}>Joukowski Transform and Conformal Mapping</h2>
      <DiagramJoukowski/>
      <Table2 rows={[
        ["Conformal mapping","Analytic complex function w = f(z) preserves angles and Laplace equation\nMaps one flow domain to another — solution in w-plane gives solution in z-plane"],
        ["Joukowski transform","w = z + c²/z  (c = transform parameter)\nMaps circle |z| = R to aerofoil in w-plane\nChord = 4c; camber and thickness from offset of circle centre"],
        ["Kutta condition","Flow must leave the trailing edge smoothly (no singularity)\nUniquely fixes Γ: the only circulation that gives finite velocity at TE\nPhysically enforced by viscosity in real flows"],
        ["Thin aerofoil theory","Linearise for small camber and angle of attack\nC_L = 2π(α + camber integral);  dC_L/dα = 2π/rad\nC_m,c/4 depends only on camber distribution — independent of α"],
        ["Blasius theorem","Lift/drag from complex potential: L − iD = iρ/2 ∮ (dw/dz)² dz\nKutta-Joukowski: L = ρU∞Γ  (per unit span)\nAllows force calculation without integrating pressure distribution"],
        ["Schwarz-Christoffel","Maps interior of polygon to upper half-plane\nUsed for channel flows, flows over steps, and dam seepage problems"],
        ["Circle theorem (Milne-Thomson)","Adding a body to a uniform flow: replace φ(z) with φ(z) + φ(c²/z̄)\nInduces correct no-penetration BC on circle without resolving BL"],
        ["Limitations","Valid only for irrotational, inviscid, incompressible, attached flow\nFails whenever separation occurs or Mach effects are significant\nProvides excellent starting point and BCs for viscous solvers"],
      ]}/>

      <h2 style={S.h2}>Stream Function and Velocity Potential</h2>
      <DiagramStreamFunction/>
      <Table2 rows={[
        ["Stream function ψ (2D)","u = ∂ψ/∂y,  v = −∂ψ/∂x\nAutomatically satisfies continuity (∇·u = 0)\nFlow rate Q₁₂ = ψ₂ − ψ₁  (between any two streamlines)"],
        ["Velocity potential φ","u = ∂φ/∂x,  v = ∂φ/∂y\nAutomatically satisfies irrotationality (ω = 0)\nOnly exists for irrotational flows"],
        ["Complex potential w(z)","w = φ + iψ;  z = x + iy\ndw/dz = u − iv  (complex conjugate velocity)\nEvery analytic function gives a valid potential flow"],
        ["Stagnation points","Where |dw/dz| = 0: u = v = 0\nStream function and potential lines meet at 90° at all other points\nStagnation pressure = p + ½ρU²"],
        ["Flow net","Graphical construction of equipotential and streamline grids\nSquares formed where grids are fine enough;  velocity ∝ 1/spacing\nHistorically used before CFD for seepage, groundwater, electrostatics"],
        ["Vorticity transport (viscous)","Dω/Dt = (ω·∇)u + ν∇²ω\nVorticity is generated at walls and diffuses/convects — potential flow fails near walls"],
        ["Helmholtz decomposition","Any vector field: u = ∇φ + ∇×A\nIrrotational part + solenoidal part\nBasis for decomposing velocity into potential and rotational contributions"],
        ["Biot-Savart law","u(x) = (Γ/4π) ∮ dl × r̂/r²  (vortex filament)\nAnalogue of magnetic field from current;  used in vortex methods"],
      ]}/>

      <h2 style={S.h2}>Computational Fluid Dynamics — Workflow and Methods</h2>
      <p style={S.p}>
        Computational Fluid Dynamics numerically solves the governing partial differential equations (Navier–Stokes, continuity, energy) on a discrete mesh. The finite volume method (FVM) — used in OpenFOAM, ANSYS Fluent, Star-CCM+ — integrates the PDEs over control volumes to produce algebraic equations for each cell. The finite element method (FEM) is favoured for structural and low-Reynolds flows. Finite difference (FDM) remains dominant in DNS and some LES codes due to its high-order accuracy on structured grids.
      </p>
      <DiagramCFDPipeline/>
      <DiagramFVM/>
      <Table2 rows={[
        ["Discretisation schemes","Upwind (1st order, stable, diffusive);  central difference (2nd order, less diffusive, may oscillate)\nQUICK (3rd order, quadratic upstream);  bounded high-resolution schemes (e.g., van Leer, MUSCL)"],
        ["Pressure-velocity coupling","SIMPLE: Semi-Implicit Method for Pressure-Linked Equations\nPISO: Pressure-Implicit with Splitting of Operators (transient)\nCoupled solver: simultaneous p-v solution; faster convergence"],
        ["Convergence criteria","Residuals normalised < 10⁻⁴ (engineering) or 10⁻⁶ (research)\nMonitor key integral quantities (drag, lift, Nusselt) to plateau\nResiduals alone can mislead — always check monitor plots"],
        ["Grid independence study","Run 3 grids: coarse, medium, fine (refine by factor ~1.5–2 in each direction)\nRichardson extrapolation: φ_exact ≈ φ_fine + (φ_fine−φ_medium)/(r^p−1)\nGrid Convergence Index (GCI) quantifies numerical uncertainty"],
        ["Courant number (CFL)","CFL = u·Δt/Δx\nExplicit time-stepping: CFL < 1 required for stability\nImplicit schemes: CFL can exceed 1 but accuracy degrades"],
        ["Turbulence BCs","Inlet: specify k and ε from Tu and length scale: k=(3/2)(TuŪ)², ε=Cμ^(3/4)k^(3/2)/l\nWalls: no-slip (u=0); k=0; ε or ω from wall distance"],
        ["Validation & verification","Verification: solving the equations correctly (grid convergence, code testing)\nValidation: solving the correct equations (match experiment)\nBoth are mandatory for publication and engineering use"],
        ["HPC and parallelisation","Domain decomposition (MeTiS);  MPI communication between subdomains\nTypical: 10⁵–10⁸ cells; modern CFD uses 100s to 10000s of CPU cores\nGPU acceleration: 10–50× speedup for explicit LES solvers"],
      ]}/>

      <h2 style={S.h2}>Multiphase Flow — Regimes and Physics</h2>
      <p style={S.p}>
        Multiphase flows — involving two or more distinct phases (gas-liquid, liquid-liquid, gas-solid, or flows with phase change) — are ubiquitous in nature and industry. The behaviour depends strongly on the void fraction (volume occupied by the dispersed phase), the velocity ratio between phases, and the interfacial forces (surface tension, drag, lift). The flow regime — whether bubbles are dispersed, coalesced into slugs, stratified by gravity, or form an annular configuration — qualitatively changes the pressure drop, heat transfer, and mechanical loads on the containing structure.
      </p>
      <DiagramMultiphaseRegimes/>
      <Table2 rows={[
        ["Void fraction α","Volume fraction of gas phase: α = V_gas / V_total\nα < 0.1: bubbly;  α ≈ 0.3–0.6: slug/churn;  α > 0.8: annular"],
        ["Slip ratio","S = U_gas / U_liquid (velocity ratio between phases)\nS = 1: homogeneous mixture;  S > 1: gas leads liquid (typical in upward flow)"],
        ["Drift flux model","U_gas = C₀ U_mixture + U_drift  (Zuber-Findlay)\nC₀ ≈ 1.2 (bubbly/slug);  U_drift = bubble rise velocity\nSimple, fast alternative to full two-fluid model"],
        ["Flow regime maps","Baker chart (horizontal);  Taitel-Dukler (mechanistic model)\nPlot superficial gas velocity vs liquid velocity\nTransition boundaries empirical — Re, Fr, We dependent"],
        ["Two-phase pressure drop","Lockhart-Martinelli correlator: φ_L² = 1 + C/X + 1/X²\nX = (dp/dz)_L/(dp/dz)_G;  C depends on flow regime\nMost widely used simple correlation in pipe flow"],
        ["Critical heat flux","Maximum heat flux before departure from nucleate boiling (DNB)\nVoid fraction sudden jump → film boiling → wall overheating\nCritical in nuclear reactor and boiler design"],
        ["Slug flow dynamics","Liquid slug length ~ 10–30D;  slug frequency ~ 1–5 Hz in large pipes\nLarge forces on bends and tees;  fatigue of piping systems\nDamped by long slug catchers in oil-gas production"],
        ["Fluidised beds","Gas flowing upward through packed solid particles\nAt U_mf (minimum fluidisation velocity): bed expands, particles suspend\nBubbling, turbulent, fast fluidisation regimes with increasing U"],
      ]}/>

      <h2 style={S.h2}>Multiphase CFD Modelling</h2>
      <DiagramMultiphaseModels/>
      <Table2 rows={[
        ["Euler-Euler interphase drag","F_D = (3/4)(C_D/d_b)α_g α_l ρ_l |u_g − u_l|(u_g − u_l)\nSchiller-Naumann: C_D = max(24/Re_b(1+0.15Re_b^0.687), 0.44)\nLift and virtual mass forces also significant for bubbles"],
        ["Euler-Lagrange forces","m_p du_p/dt = F_drag + F_gravity + F_lift + F_Basset + F_virtual mass\nStokes drag: F_D = 3πμd_p(u − u_p)\nTwo-way coupling: particle feeds back force to fluid (dense flows)"],
        ["VOF interface reconstruction","PLIC (Piecewise Linear Interface Calculation): find plane in each cell\nLevel-set: φ = signed distance; smooth interface; mass conservation issue\nPhase-field (Cahn-Hilliard): thermodynamically consistent; diffuse interface"],
        ["Surface tension CSF model","Continuum Surface Force (Brackbill): F_σ = σκ∇α  (κ = curvature = ∇·n̂)\nSpreads force over 2–3 cells; parasitic currents near interface\nSharp-surface-force (SSF) reduces spurious currents"],
        ["Boiling and condensation","Phase change at interface: ṁ = h_fg⁻¹ Q_wall  (simple energy balance)\nLee model: ṁ = r_coeff α_l ρ_l(T−T_sat)/T_sat  (adjustable time relaxation)\nNucleate boiling: Rohsenow correlation for wall flux → bubble departure"],
        ["Population balance (PBE)","Track bubble/droplet size distribution n(V,t)\n∂n/∂t + ∇·(un) = B_birth − D_death  (coalescence and breakup)\nCoupled with two-fluid model for poly-disperse bubbly flows"],
        ["DEM coupling (CFD-DEM)","Discrete Element Method tracks each particle as rigid body\nCFD provides fluid drag; DEM gives particle-particle contacts\nEssential for fluidised beds, mixing vessels, granular flows"],
        ["LBM for multiphase","Lattice Boltzmann: mesoscopic kinetic approach; easy multiphase implementation\nShan-Chen model: pseudo-potential separates phases\nEfficient on GPU;  limited Re range;  growing for microfluidics and porous media"],
      ]}/>

      <h2 style={S.h2}>Surface Tension, Capillarity and Droplet Physics</h2>
      <DiagramSurfaceTension/>
      <Table2 rows={[
        ["Surface tension σ","σ = dF/dL = dE/dA  (N/m = J/m²)\nWater-air at 20°C: σ = 0.0728 N/m;  mercury-air: σ = 0.487 N/m\nDecreases with temperature; surfactants reduce σ significantly"],
        ["Young-Dupré equation","σ_SG = σ_SL + σ_LG cosθ\nθ < 90°: wetting (hydrophilic);  θ > 90°: non-wetting (hydrophobic)\nθ ≈ 0: spreading;  θ ≈ 180°: perfect non-wetting (lotus effect)"],
        ["Young-Laplace equation","Δp = σ(1/R₁ + 1/R₂) = 2σ/R (sphere)\nPressure jump across curved interface\nBubble: Δp = 4σ/R (two interfaces);  droplet: Δp = 2σ/R"],
        ["Weber number","We = ρU²L/σ  (inertia / surface tension)\nWe < 1: surface tension dominant (droplet stays spherical)\nWe > 10: droplet breakup; spray atomisation"],
        ["Bond number","Bo = ρgL²/σ  (gravity / surface tension)\nBo < 1: capillary-dominated (small drops, microfluidics)\nBo > 10: gravity-dominated (large pools, pipe flow)"],
        ["Capillary number","Ca = μU/σ  (viscous / surface tension)\nCa < 0.1: interface not significantly deformed by viscosity\nRelevant in microfluidics, droplet microfluidics, EOR"],
        ["Marangoni flow","Flow driven by surface tension gradients (due to T or concentration)\n∂σ/∂T < 0 for most liquids: hot spots attract flow from low-T regions\nCritical in welding, crystal growth, thin-film dynamics"],
        ["Rayleigh-Plateau instability","Liquid jet breaks into droplets when wavelength λ > 2πr (Rayleigh 1878)\nGrowth rate σ_RT = σ/(ρr³)^(1/2) × f(Oh)\nBasis of inkjet printing, drip tip formation, fuel injection"],
      ]}/>

      <h2 style={S.h2}>Large Eddy Simulation and DNS</h2>
      <Table2 rows={[
        ["LES filtering","Decompose: ū (resolved large eddies) + u′′ (sub-grid scale)\nFilter width Δ ~ mesh size;  ū solved, u′′ modelled\nFilter: ū(x) = ∫ G(x−x′) u(x′) dx′  (Gaussian, top-hat, or sharp spectral)"],
        ["Smagorinsky SGS model","τ_ij^SGS = −2C_s²Δ²|S̄|S̄_ij\nC_s ≈ 0.1 (channel), 0.2 (isotropic);  simple, stable, over-dissipative"],
        ["Dynamic Smagorinsky","C_s computed locally using Germano identity at two filter levels\nMore accurate: C_s adapts to local flow state; can be negative (backscatter)\nStandard in high-fidelity LES today"],
        ["Wall-resolved LES cost","Δx+ < 100, Δz+ < 30, Δy+ ~ 1;  cost ~ Re^{1.8}\nPractical Re < 10⁵ in academic research;  entire aircraft impractical"],
        ["DNS","Resolve ALL scales from L down to η;  ∇²φ = 0 model-free\nCost ~ Re^{9/4} (3D);  Re ~ 10⁴ possible on petascale machines\nGold standard for model development and fundamental research"],
        ["Spectral methods for DNS","Fourier expansion in homogeneous directions;  Chebyshev in wall-normal\nSpectral accuracy (exponential convergence);  channel flow benchmark\nPseudo-spectral: dealiasing via 3/2 rule"],
        ["Synthetic turbulence inflow","LES/DNS need physically consistent turbulent inflow (not white noise)\nDavidson (synthetic eddies);  recycling-rescaling method\nEnsure correct length scales and energy spectrum at inlet"],
        ["Hybrid RANS-LES","DDES (Delayed Detached Eddy Simulation): RANS in attached BL, LES in wake\nSAS (Scale-Adaptive Simulation): adjusts νt based on local length scale\nBalance between cost and accuracy for industrial separated flows"],
      ]}/>

      <h2 style={S.h2}>Special Topics and Applications</h2>
      <Table2 rows={[
        ["Magnetohydrodynamics (MHD)","Coupling of fluid motion and magnetic fields\nLorentz force: F = J × B added to NS momentum\nApplications: liquid metal cooling, plasma fusion, electromagnetic pumps"],
        ["Non-Newtonian fluids","Newtonian: τ = μ(du/dy);  Non-Newtonian: τ = f(du/dy)\nPower-law: τ = K(du/dy)^n;  Bingham plastic: τ = τ_y + μ_p(du/dy)\nBlood, polymers, gels, cement — all non-Newtonian"],
        ["Microfluidics","Re ≪ 1: Stokes flow;  inertia negligible;  Re ~ 10⁻³–1\nCapillary number Ca, Bond Bo dominate over Re\nLab-on-chip: droplet generation, mixing, sorting"],
        ["Acoustic streaming","Steady flow driven by attenuation of sound waves\nRayleigh streaming (outside BL);  Schlichting streaming (inside BL)\nApplications: ultrasonic cleaning, atomisation, microfluidic mixing"],
        ["Geophysical fluid dynamics","Rotating flows: Coriolis force 2Ω×u dominates at large scales\nRossby number Ro = U/(ΩL) ≪ 1: geostrophic balance\nWeather, ocean currents, atmospheric jets"],
        ["Porous media flow","Darcy's law: u = −(K/μ)∇p  (K = permeability, m²)\nForchheimer correction at higher Re: adds quadratic inertia term\nOil and gas reservoir, groundwater, filters, fuel cells"],
        ["Aeroelasticity","Coupling of fluid and structural dynamics: lift, drag, flutter, buffeting\nFlutter: coupled fluid-structure instability → divergent oscillation\nFrequency ratio ωs/ωf, mass ratio m* are key parameters"],
        ["Uncertainty quantification","Monte Carlo, polynomial chaos, Bayesian inference applied to CFD\nPropagate geometric, BC, and turbulence model uncertainties\nEssential for design under uncertainty and digital twins"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Kolmogorov microscale:  η = (ν³/ε)^(1/4);  inertial spectrum:  E(k) = Cε^(2/3)k^(−5/3)",
        "Reynolds decomposition:  u = Ū + u′;  Reynolds stress:  τ_ij = −ρ⭨u′ᵢu′ⱼ⭩",
        "Boussinesq hypothesis:  τ_ij = μ_t(∂Ūᵢ/∂xⱼ + ∂Ūⱼ/∂xᵢ) − (2/3)ρk δᵢⱼ",
        "k-ε turbulent viscosity:  νt = Cμ k²/ε  (Cμ = 0.09)",
        "Laplace equation (potential flow):  ∇²φ = 0;  u = ∇φ",
        "Kutta-Joukowski lift:  L = ρU∞Γ per unit span",
        "Joukowski transform:  w = z + c²/z",
        "Jurin's law (capillary rise):  h = 2σ cosθ / (ρgr)",
        "Young-Laplace (pressure jump):  Δp = σ(1/R₁ + 1/R₂)",
        "Weber number:  We = ρU²L/σ;  Bond number:  Bo = ρgL²/σ",
        "VOF advection:  ∂F/∂t + u·∇F = 0;  surface tension CSF:  F_σ = σκ∇α",
        "GCI (grid convergence):  GCI = Fs|ε|/(r^p − 1)  (Fs=1.25 safety factor)",
      ]}/>
    </div>
  );
}